import React, { useEffect, useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, ClinicalNote, LabRequest, RadiologyRequest, Prescription } from '../../types';
import { Stethoscope, FileText, FlaskConical, Scan, Pill, CheckCircle, History, X, ZoomIn, ZoomOut, Eye, Printer, Download, Trash2, RotateCcw } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { SickNoteForm } from './SickNoteForm';
/**
 * Capture a DOM element and export it as a properly paginated A4 PDF.
 * A4 layout: 210mm × 297mm, margins 20mm top/bottom, 18mm left/right.
 */
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isAMHTest, hcgStructure, isHCGTest, isHCGAbnormal, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest } from '../../store/labStructures';
type CViewingTest = {
  kind: 'lab';
  labId: string;
  testName?: string;
} | {
  kind: 'rad';
  radId: string;
} | null;
export const ClinicianDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser,
    getPatientEncounters,
    logAudit
  } = useData();
  const [activeTab, setActiveTab] = useState<'queue' | 'completed'>('queue');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEncounterId, setSelectedEncounterId] = useState<string | null>(null);
  // Live encounter - always reads from context so lab/rad updates appear instantly
  const selectedEncounter = selectedEncounterId ? encounters.find(e => e.id === selectedEncounterId) || null : null;
  // Image zoom state
  const [zoomImage, setZoomImage] = useState<string | null>(null);
  // View Test File modal state
  const [viewingTest, setViewingTest] = useState<CViewingTest>(null);
  const [testImageZoom, setTestImageZoom] = useState<number>(100);
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);
  const testModalRef = useRef<HTMLDivElement>(null);
  // Remove Test Modal state
  const [removeTestModal, setRemoveTestModal] = useState<{
    isOpen: boolean;
    labId: string;
    testName: string;
    reason: string;
  }>({
    isOpen: false,
    labId: '',
    testName: '',
    reason: ''
  });
  const closeTestModal = () => {
    setViewingTest(null);
    setTestImageZoom(100);
  };
  const handlePrintTestModal = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadTestPdf = async () => {
    if (!testModalRef.current) return;
    setIsGeneratingTestPdf(true);
    try {
      const filename = `BMED_TestResult_${Date.now()}.pdf`;
      await captureToA4Pdf(testModalRef.current, filename);
    } catch (err) {
      console.error('Test PDF failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingTestPdf(false);
    }
  };
  // Modals state
  const [showLabModal, setShowLabModal] = useState(false);
  const [showRadModal, setShowRadModal] = useState(false);
  const [showRxModal, setShowRxModal] = useState(false);
  const [showSickNoteModal, setShowSickNoteModal] = useState(false);
  // Clinical Notes State
  const [notes, setNotes] = useState<Partial<ClinicalNote>>({
    presentingComplaint: '',
    historyOfPresentingComplaint: '',
    pastMedicalHistory: [],
    drugHistory: '',
    familyMedicalHistory: [],
    obgynHistory: '',
    socialHistory: {
      alcohol: '',
      smoking: '',
      occupation: '',
      mentalStatus: ''
    },
    reviewOfSystems: {
      rs: '',
      cvs: '',
      cns: '',
      git: '',
      gut: '',
      mss: '',
      is: ''
    },
    onExamination: '',
    onExaminationDetails: {
      gc: '',
      febrile: '',
      pjc: '',
      s1s2: '',
      rs: '',
      pa: ''
    },
    significantFindings: [],
    workingDiagnosis: []
  });
  const [reviewDateNotes, setReviewDateNotes] = useState('');
  // Lab Request State
  const [selectedTests, setSelectedTests] = useState<string[]>([]);
  const [otherTests, setOtherTests] = useState('');
  // Rad Request State
  const [scanType, setScanType] = useState('');
  // Rx State
  const [rxForm, setRxForm] = useState({
    drugName: '',
    dose: '',
    route: '',
    frequency: '',
    days: ''
  });
  // Active queue only. Completed visits live in Patient Files and can be
  // reopened from there via the "Amend File" flow.
  const pendingEncounters = encounters.filter(e => e.stage !== 'COMPLETED');
  const completedEncounters = encounters.filter(e => e.stage === 'COMPLETED');
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const filteredCompleted = completedEncounters.filter(enc => {
    const p = getPatient(enc.patientId);
    if (!p) return false;
    const q = searchQuery.toLowerCase();
    return p.firstName.toLowerCase().includes(q) || p.lastName.toLowerCase().includes(q) || p.bmcFileNumber.toLowerCase().includes(q);
  }).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  const handleSelectEncounter = (encounter: Encounter) => {
    setSelectedEncounterId(encounter.id);
    setReviewDateNotes(encounter.reviewDateNotes || '');
    if (encounter.clinicalNote) {
      setNotes(encounter.clinicalNote);
    } else {
      setNotes({
        presentingComplaint: '',
        historyOfPresentingComplaint: '',
        pastMedicalHistory: [],
        drugHistory: '',
        familyMedicalHistory: [],
        obgynHistory: '',
        socialHistory: {
          alcohol: '',
          smoking: '',
          occupation: '',
          mentalStatus: ''
        },
        reviewOfSystems: {
          rs: '',
          cvs: '',
          cns: '',
          git: '',
          gut: '',
          mss: '',
          is: ''
        },
        onExamination: '',
        onExaminationDetails: {
          gc: '',
          febrile: '',
          pjc: '',
          s1s2: '',
          rs: '',
          pa: ''
        },
        significantFindings: [],
        workingDiagnosis: []
      });
    }
    // Move encounter back to CLINICIAN stage if returning from investigations
    if (encounter.stage === 'INVESTIGATIONS') {
      updateEncounter(encounter.id, {
        stage: 'CLINICIAN',
        lastAttendingClinician: currentUser?.fullName
      });
    } else if (currentUser && encounter.lastAttendingClinician !== currentUser.fullName) {
      // Record that this clinician opened/consulted the file.
      updateEncounter(encounter.id, {
        lastAttendingClinician: currentUser.fullName
      });
    }
  };
  const handleSaveNotes = () => {
    if (!selectedEncounter || !currentUser) return;
    const isAmending = selectedEncounter.stage === 'COMPLETED';
    let editHistory = selectedEncounter.clinicalNote?.editHistory || [];
    if (isAmending) {
      const reason = prompt('Please enter a mandatory reason for modifying this completed consultation:');
      if (!reason || !reason.trim()) {
        alert('A reason is required to amend a completed consultation.');
        return;
      }
      const previousNote = selectedEncounter.clinicalNote!;
      const {
        editHistory: _oldHistory,
        ...noteSnapshot
      } = previousNote;
      editHistory = [...editHistory, {
        editedAt: new Date().toISOString(),
        editedBy: currentUser.id,
        editedByName: currentUser.fullName,
        reason: reason.trim(),
        previousNote: noteSnapshot as any
      }];
      logAudit({
        type: 'consultation.edit',
        encounterId: selectedEncounter.id,
        patientId: selectedEncounter.patientId,
        patientName: `${getPatient(selectedEncounter.patientId)?.firstName} ${getPatient(selectedEncounter.patientId)?.lastName}`,
        userId: currentUser.id,
        userName: currentUser.fullName,
        userRole: currentUser.role,
        reason: reason.trim(),
        previousContent: JSON.stringify(noteSnapshot),
        newContent: JSON.stringify(notes)
      });
    }
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: isAmending ? selectedEncounter.clinicalNote!.recordedAt : new Date().toISOString(),
      recordedBy: isAmending ? selectedEncounter.clinicalNote!.recordedBy : currentUser.id,
      clinicianName: isAmending ? selectedEncounter.clinicalNote!.clinicianName : currentUser.fullName,
      editHistory
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName
    });
    alert(isAmending ? 'Amendment saved and logged successfully.' : 'Clinical notes saved successfully.');
  };
  const handleRequestLab = () => {
    if (!selectedEncounter || !currentUser || selectedTests.length === 0 && !otherTests) return;
    const newLab: LabRequest = {
      id: `lab-${Date.now()}`,
      tests: selectedTests,
      otherTests,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      labRequests: [...selectedEncounter.labRequests, newLab],
      stage: 'INVESTIGATIONS',
      pendingLab: true
    });
    setShowLabModal(false);
    setSelectedTests([]);
    setOtherTests('');
    // File stays open — clinician closes/saves/completes when ready.
    alert('Laboratory tests requested. The patient file remains open — you can continue your consultation.');
  };
  const handleRequestRad = () => {
    if (!selectedEncounter || !currentUser || !scanType) return;
    const newRad: RadiologyRequest = {
      id: `rad-${Date.now()}`,
      scanType,
      requestedAt: new Date().toISOString(),
      requestedBy: currentUser.id,
      status: 'Pending'
    };
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: [...selectedEncounter.radiologyRequests, newRad],
      stage: 'INVESTIGATIONS',
      pendingRadiology: true
    });
    setShowRadModal(false);
    setScanType('');
    // File stays open — clinician closes/saves/completes when ready.
    alert('Radiology scan requested. The patient file remains open — you can continue your consultation.');
  };
  const handleAddRx = () => {
    if (!selectedEncounter || !currentUser || !rxForm.drugName) return;
    if (selectedEncounter.prescriptions.length >= 20) {
      alert('You have reached the maximum of 20 drugs per prescription. Remove an existing item before adding another.');
      return;
    }
    const newRx: Prescription = {
      id: `rx-${Date.now()}`,
      ...rxForm,
      status: 'Pending',
      prescribedAt: new Date().toISOString(),
      prescribedBy: currentUser.id
    };
    updateEncounter(selectedEncounter.id, {
      prescriptions: [...selectedEncounter.prescriptions, newRx]
    });
    setRxForm({
      drugName: '',
      dose: '',
      route: '',
      frequency: '',
      days: ''
    });
  };
  // Delete a single prescription. Only allowed while it is still Pending —
  // once Pharmacy has dispensed (or marked unavailable), it stays on record.
  const handleDeletePrescription = (rxId: string) => {
    if (!selectedEncounter) return;
    const rx = selectedEncounter.prescriptions.find(p => p.id === rxId);
    if (!rx) return;
    if (rx.status !== 'Pending') {
      alert('This drug has already been processed by Pharmacy and cannot be removed from the record.');
      return;
    }
    if (!confirm(`Remove "${rx.drugName}" from the prescription list? This cannot be undone.`)) return;
    updateEncounter(selectedEncounter.id, {
      prescriptions: selectedEncounter.prescriptions.filter(p => p.id !== rxId)
    });
  };
  // Recall (remove) a single ordered lab test that has not yet been completed.
  // - For tests inside lab.tests[]: drop just that test from the array.
  // - If that empties the request (and no otherTests), remove the whole request.
  // - For an "other test" entry: clear lab.otherTests on that request, or
  //   remove the request entirely if it has no tests left either.
  const handleRecallLabTest = (labId: string, testName: string, isOther = false) => {
    if (!selectedEncounter) return;
    const lab = selectedEncounter.labRequests.find(l => l.id === labId);
    if (!lab) return;
    if (lab.status === 'Completed') {
      alert('This test has already been completed by the laboratory and cannot be recalled.');
      return;
    }
    if (!confirm(`Recall the lab test "${testName}"? The laboratory will no longer see this request.`)) return;
    let updatedLabs = selectedEncounter.labRequests.map(l => {
      if (l.id !== labId) return l;
      if (isOther) return {
        ...l,
        otherTests: ''
      };
      return {
        ...l,
        tests: l.tests.filter(t => t !== testName)
      };
    });
    // Drop any request that now has no tests AND no otherTests.
    updatedLabs = updatedLabs.filter(l => l.tests.length > 0 || l.otherTests.trim().length > 0);
    const stillPendingLab = updatedLabs.some(l => l.status === 'Pending');
    updateEncounter(selectedEncounter.id, {
      labRequests: updatedLabs,
      pendingLab: stillPendingLab
    });
  };
  // Recall (remove) a pending radiology request.
  const handleRecallRadiology = (radId: string) => {
    if (!selectedEncounter) return;
    const rad = selectedEncounter.radiologyRequests.find(r => r.id === radId);
    if (!rad) return;
    if (rad.status === 'Completed') {
      alert('This scan has already been completed by radiology and cannot be recalled.');
      return;
    }
    if (!confirm(`Recall the radiology request "${rad.scanType}"? Radiology will no longer see this request.`)) return;
    const updatedRads = selectedEncounter.radiologyRequests.filter(r => r.id !== radId);
    const stillPendingRad = updatedRads.some(r => r.status === 'Pending');
    updateEncounter(selectedEncounter.id, {
      radiologyRequests: updatedRads,
      pendingRadiology: stillPendingRad
    });
  };
  const handleRemoveTestConfirm = () => {
    if (!selectedEncounter || !currentUser || !removeTestModal.reason.trim()) return;
    const lab = selectedEncounter.labRequests.find(l => l.id === removeTestModal.labId);
    if (!lab) return;
    const now = new Date().toISOString();
    const newEntry = {
      testName: removeTestModal.testName,
      removedAt: now,
      removedBy: currentUser.id,
      reason: removeTestModal.reason
    };
    const updatedLabRequests = selectedEncounter.labRequests.map(l => {
      if (l.id !== removeTestModal.labId) return l;
      return {
        ...l,
        removedTestNames: [...(l.removedTestNames || []), removeTestModal.testName],
        removedTestEntries: [...(l.removedTestEntries || []), newEntry]
      };
    });
    updateEncounter(selectedEncounter.id, {
      labRequests: updatedLabRequests
    });
    const patient = patients.find(p => p.id === selectedEncounter.patientId);
    logAudit({
      type: 'lab.test.remove',
      encounterId: selectedEncounter.id,
      patientId: selectedEncounter.patientId,
      patientName: patient ? `${patient.firstName} ${patient.lastName}` : 'Unknown',
      userId: currentUser.id,
      userName: currentUser.fullName,
      userRole: currentUser.role,
      reason: removeTestModal.reason,
      labRequestId: removeTestModal.labId,
      testName: removeTestModal.testName
    });
    setRemoveTestModal({
      isOpen: false,
      labId: '',
      testName: '',
      reason: ''
    });
  };
  const handleCompleteVisit = () => {
    if (!selectedEncounter || !currentUser) return;
    // Save notes one last time
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    const hasPrescriptions = selectedEncounter.prescriptions.length > 0;
    const hasPendingDispense = selectedEncounter.prescriptions.some(rx => rx.status === 'Pending');
    // If prescriptions exist and none have been dispensed yet, send to pharmacy.
    // If pharmacy has already processed them (returned to clinician), complete the visit.
    const sendToPharmacy = hasPrescriptions && hasPendingDispense;
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName,
      stage: sendToPharmacy ? 'PHARMACY' : 'COMPLETED',
      completedAt: sendToPharmacy ? undefined : new Date().toISOString()
    });
    alert(sendToPharmacy ? 'Visit saved. Patient forwarded to Pharmacy.' : 'Visit completed successfully.');
    setSelectedEncounterId(null);
  };
  const handleSendToPharmacy = () => {
    if (!selectedEncounter || !currentUser) return;
    const pendingRx = selectedEncounter.prescriptions.filter(rx => rx.status === 'Pending');
    if (pendingRx.length === 0) {
      alert('No pending prescriptions to send to pharmacy.');
      return;
    }
    // Save current notes
    const clinicalNote: ClinicalNote = {
      ...(notes as ClinicalNote),
      recordedAt: new Date().toISOString(),
      recordedBy: currentUser.id,
      clinicianName: currentUser.fullName
    };
    updateEncounter(selectedEncounter.id, {
      clinicalNote,
      reviewDateNotes,
      lastAttendingClinician: currentUser.fullName,
      stage: 'PHARMACY'
    });
    // File stays open — clinician decides when to close, save draft, or complete the visit.
    alert(`Prescription sent to Pharmacy (${pendingRx.length} drug${pendingRx.length > 1 ? 's' : ''}). The patient file remains open — you can continue your consultation.`);
  };
  if (selectedEncounter) {
    const patient = getPatient(selectedEncounter.patientId);
    if (!patient) return <div data-id="element-2080" data-mp="21ddoxjge2s8q">Patient not found</div>;
    const isCompleted = selectedEncounter.stage === 'COMPLETED';
    const isOriginalClinician = selectedEncounter.clinicalNote?.recordedBy === currentUser?.id;
    const isAdmin = currentUser?.role === 'Administrator';
    const canEdit = !isCompleted || isOriginalClinician || isAdmin;
    return <div className="space-y-6 pb-20" data-id="element-2081" data-mp="1ilb37tow4lrm">
        {isCompleted && !canEdit && <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded flex items-center mb-4" data-id="element-2082" data-mp="1ij8ere3dz0p0">
            <span className="text-sm font-medium" data-id="element-2083" data-mp="4p1gs19aa50q">
              This consultation is completed and can only be amended by the
              original clinician (
              {selectedEncounter.clinicalNote?.clinicianName}) or an
              Administrator.
            </span>
          </div>}
        {isCompleted && canEdit && <div className="bg-amber-50 border border-amber-200 text-amber-800 px-4 py-3 rounded flex items-center mb-4" data-id="element-2084" data-mp="3mtuupv2gy3tv">
            <History className="w-5 h-5 mr-2 text-amber-600" data-id="element-2085" data-mp="3hwuqlpp6jb5e" />
            <span className="text-sm font-medium" data-id="element-2086" data-mp="3d4l6iqaqx5b1">
              You are viewing a completed consultation. Any edits saved will be
              logged in the audit trail.
            </span>
          </div>}
        {/* Sticky Action Bar */}
        <div className="flex justify-between items-center sticky top-0 bg-gray-100 z-10 py-4 border-b border-gray-200" data-id="element-2087" data-mp="342258amlz0qw">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-2088" data-mp="2u68t2scsx99n">
            {isCompleted ? 'Completed Consultation' : 'Consultation'}
          </h1>
          <div className="flex space-x-2" data-id="element-2089" data-mp="2khlg5lf6pdjh">
            <Button variant="outline" onClick={() => setSelectedEncounterId(null)} data-id="element-2090" data-mp="2phg4bh7krp0u">
              Close File
            </Button>
            <Button variant="outline" className="text-amber-700 border-amber-600 hover:bg-amber-50" onClick={() => {
            const reason = prompt('Send this file back to Nursing so the vitals can be corrected. Please provide a brief reason (optional):', '') ?? '';
            if (confirm('Are you sure you want to send this file back to Nursing? The patient will leave your queue until the nurse re-submits the corrected vitals.')) {
              const notePrefix = reason ? `[Returned to Nursing by ${currentUser?.fullName || 'Clinician'}: ${reason}]` : `[Returned to Nursing by ${currentUser?.fullName || 'Clinician'}]`;
              updateEncounter(selectedEncounter.id, {
                stage: 'NURSING',
                vitalsReturnNote: notePrefix,
                vitalsReturnedAt: new Date().toISOString()
              } as any);
              alert('File sent back to Nursing for vitals correction. It will return to your queue once the nurse re-submits the vitals.');
              setSelectedEncounterId(null);
            }
          }} data-id="element-2091" data-mp="2bjhsaigq84oi">
              ↩ Send to Nursing
            </Button>
            <Button variant="outline" onClick={() => setShowSickNoteModal(true)} data-id="element-2092" data-mp="11j5n2hei7ub1">
              <FileText className="w-4 h-4 mr-2" data-id="element-2093" data-mp="2hwvofge8o4gz" /> Generate Sick Note
            </Button>
            <Button variant="primary" onClick={handleSaveNotes} data-id="element-2094" data-mp="2czna5qw7okl0">
              {selectedEncounter.stage === 'COMPLETED' ? 'Save Amendment' : 'Save Draft'}
            </Button>
            {selectedEncounter.stage !== 'COMPLETED' && <Button variant="success" className="bg-green-600 text-white hover:bg-green-700" onClick={handleCompleteVisit} data-id="element-2095" data-mp="vietrfuus0vy">
                <CheckCircle className="w-4 h-4 mr-2" data-id="element-2096" data-mp="10f5920yu2sgb" /> Complete Visit
              </Button>}
          </div>
        </div>

        {showSickNoteModal && <SickNoteForm encounter={selectedEncounter} patient={patient} clinician={currentUser} onClose={() => setShowSickNoteModal(false)} data-id="element-2097" data-mp="1ae4df7ckg1xi" />}

        {/* Medical Form */}
        <div className="bg-white border-2 border-gray-300 rounded-lg p-6" data-id="element-2098" data-mp="1o73sj88fru4j">
          <fieldset disabled={!canEdit} className="group" data-id="element-2099" data-mp="1o4zjkbvok4xe">
            {/* ═══ FORM HEADER ═══ */}
            {selectedEncounter.lastAttendingClinician && selectedEncounter.lastAttendingClinician !== currentUser?.fullName && <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded flex items-center" data-id="element-2100" data-mp="1ya96k7c16djv">
                  <Stethoscope className="w-5 h-5 mr-2 text-blue-600" data-id="element-2101" data-mp="1tc7n3lap0c7v" />
                  <span className="text-sm font-medium" data-id="element-2102" data-mp="1pbdx8mdowncc">
                    This file was last attended to by:{' '}
                    <span className="font-bold" data-id="element-2103" data-mp="2m03k6r46c656">
                      {selectedEncounter.lastAttendingClinician}
                    </span>
                  </span>
                </div>}
            <div className="flex justify-between items-start mb-6" data-id="element-2104" data-mp="2c98vlg3ggugk">
              <div className="flex items-center" data-id="element-2105" data-mp="28a01290lcsjy">
                <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-2106" data-mp="287416f56ntsv" />
                <div data-id="element-2107" data-mp="y2kkvzbs2an6">
                  <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-2108" data-mp="y45hduhnk3no">
                    BMED MEDICAL CENTRE
                  </h1>
                  <p className="text-sm italic text-gray-600" data-id="element-2109" data-mp="odapa9hq69ub">
                    Caring Healing Leading
                  </p>
                </div>
              </div>
              <div className="text-right" data-id="element-2110" data-mp="ta14kulph1eo">
                <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-2111" data-mp="fc366yns5fkt">
                  Confidential
                </p>
                <p className="text-sm" data-id="element-2112" data-mp="k930ydf6ivwm">
                  <span className="font-semibold" data-id="element-2113" data-mp="5eyiuvi77e2f">Date:</span>{' '}
                  {new Date(selectedEncounter.startedAt).toLocaleDateString()}
                </p>
                <p className="text-sm" data-id="element-2114" data-mp="1zs6gsw1h05sa">
                  <span className="font-semibold" data-id="element-2115" data-mp="1zt95qyf8fy5k">File No:</span>{' '}
                  {patient.bmcFileNumber}
                </p>
              </div>
            </div>

            {/* ═══ PERSONAL DETAILS ═══ */}
            <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-2116" data-mp="3pa3d6y2snc2s">
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2117" data-mp="3kb07rciwj3ua">
                <div className="col-span-1 font-semibold" data-id="element-2118" data-mp="2u8w74w40v6s">1.</div>
                <div className="col-span-6" data-id="element-2119" data-mp="2vjz72hqdywu">
                  <span className="font-semibold" data-id="element-2120" data-mp="3u84xfmbyhhfu">Full Names:</span>{' '}
                  {patient.firstName} {patient.lastName}
                </div>
                <div className="col-span-3" data-id="element-2121" data-mp="bw9rhw7s975t">
                  <span className="font-semibold" data-id="element-2122" data-mp="7tljtrut26af">Sex:</span> {patient.sex}
                </div>
                <div className="col-span-2" data-id="element-2123" data-mp="lsuyuo79yuct">
                  <span className="font-semibold" data-id="element-2124" data-mp="lqigwb1iojzr">Age:</span> {patient.age}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2125" data-mp="grozi7j408ub">
                <div className="col-span-1 font-semibold" data-id="element-2126" data-mp="2nm2clt78p3wl">2.</div>
                <div className="col-span-11" data-id="element-2127" data-mp="2im6gsh8p4is4">
                  <span className="font-semibold" data-id="element-2128" data-mp="1wrpez43s3kj3">Marital Status:</span>{' '}
                  {patient.maritalStatus}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2129" data-mp="21p7pv01cw9tq">
                <div className="col-span-1 font-semibold" data-id="element-2130" data-mp="26ik54jk5b49t">3.</div>
                <div className="col-span-7" data-id="element-2131" data-mp="2bgc41xj7jqni">
                  <span className="font-semibold" data-id="element-2132" data-mp="2bg2do8u93hha">Home Address:</span>{' '}
                  {patient.address}
                </div>
                <div className="col-span-4" data-id="element-2133" data-mp="1dv7riyvyq6sn">
                  <span className="font-semibold" data-id="element-2134" data-mp="1iszhvogylkuq">Contact:</span>{' '}
                  {patient.contact}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2135" data-mp="1ntdrhay7l2uv">
                <div className="col-span-1 font-semibold" data-id="element-2136" data-mp="1srf6nksiki12">4.</div>
                <div className="col-span-11" data-id="element-2137" data-mp="2z54u5dd5f4a7">
                  <span className="font-semibold" data-id="element-2138" data-mp="2z8iukg2953f0">Occupation:</span>{' '}
                  {patient.occupation || '_________________________'}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2139" data-mp="sh4e7fd723n7">
                <div className="col-span-1 font-semibold" data-id="element-2140" data-mp="njco8qw40rln">5.</div>
                <div className="col-span-5" data-id="element-2141" data-mp="ircjml53haiq">
                  <span className="font-semibold" data-id="element-2142" data-mp="iq9qe6kauvzn">Next of Kin:</span>{' '}
                  {patient.nextOfKin}
                </div>
                <div className="col-span-3" data-id="element-2143" data-mp="8toohu3ku101">
                  <span className="font-semibold" data-id="element-2144" data-mp="8vhvxbm0kp93">Contact:</span>{' '}
                  {patient.nokContact}
                </div>
                <div className="col-span-3" data-id="element-2145" data-mp="4uplif1eoeb8">
                  <span className="font-semibold" data-id="element-2146" data-mp="3vxutkkuo1t1k">Relationship:</span>{' '}
                  {patient.nokRelationship}
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-2147" data-mp="3r1w56x9c8b15">
                <div className="col-span-1 font-semibold" data-id="element-2148" data-mp="1u56tulh8dnqj">6.</div>
                <div className="col-span-11" data-id="element-2149" data-mp="1p9htifxe02t6">
                  <span className="font-semibold" data-id="element-2150" data-mp="2fi3wt540rwhw">Allergies:</span>{' '}
                  <span className="text-red-600 font-medium" data-id="element-2151" data-mp="2fg9c9rwnu6ec">
                    {patient.allergies}
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-12 gap-2" data-id="element-2152" data-mp="2p6vjaxqb9olf">
                <div className="col-span-1 font-semibold" data-id="element-2153" data-mp="2k8u1sh8j9xc2">7.</div>
                <div className="col-span-11" data-id="element-2154" data-mp="2k9c6m299hjvl">
                  <span className="font-semibold" data-id="element-2155" data-mp="1wizoxzo7s8rt">HIV Status:</span>{' '}
                  <span className="ml-4" data-id="element-2156" data-mp="1wlc82g5pkp6g">
                    <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-2157" data-mp="1rky87le9glji">
                      Positive {patient.hivStatus === 'Positive' ? '✓' : ''}
                    </span>
                    <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${patient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-2158" data-mp="3gybdbcu76dlo">
                      Negative {patient.hivStatus === 'Negative' ? '✓' : ''}
                    </span>
                    <span className={`inline-block px-2 py-0.5 border border-black ${patient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-2159" data-mp="3ph4xv58vwx3q">
                      Unknown {patient.hivStatus === 'Unknown' ? '✓' : ''}
                    </span>
                  </span>
                </div>
              </div>
            </div>

            {/* ═══ NURSING OBSERVATIONS ═══ */}
            <div className="mb-6" data-id="element-2160" data-mp="c0fdudy461hs">
              <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-2161" data-mp="3thymk0u5ifxo">
                Nursing Observations
              </h3>
              <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-2162" data-mp="29b2do7ncyrp">
                <thead data-id="element-2163" data-mp="pyvlc73svw0z">
                  <tr className="bg-gray-50" data-id="element-2164" data-mp="urot73ukfkha">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-2165" data-mp="14o70kvv3jv0o">
                        {n}
                      </th>)}
                  </tr>
                  <tr className="bg-gray-100" data-id="element-2166" data-mp="vp3f5l8gj3j0">
                    <th className="border border-black p-1" data-id="element-2167" data-mp="qopbsghswhrb">Temp</th>
                    <th className="border border-black p-1" data-id="element-2168" data-mp="lot70f1a4map">Pulse</th>
                    <th className="border border-black p-1" data-id="element-2169" data-mp="lq4aseuq5u1t">Resp</th>
                    <th className="border border-black p-1" data-id="element-2170" data-mp="gx1kqgp5p2qd">BP</th>
                    <th className="border border-black p-1" data-id="element-2171" data-mp="byqbo86oqw97">SpO2</th>
                    <th className="border border-black p-1" data-id="element-2172" data-mp="bwx6yyfmum3u">Weight (Kg)</th>
                    <th className="border border-black p-1" data-id="element-2173" data-mp="7yzgzte1fvze">Height</th>
                    <th className="border border-black p-1" data-id="element-2174" data-mp="2yl683l1y7xo">BMI</th>
                    <th className="border border-black p-1" data-id="element-2175" data-mp="3u4mjmishpdmh">Pain Score</th>
                    <th className="border border-black p-1" data-id="element-2176" data-mp="3u6plc0308v71">AVPU</th>
                  </tr>
                </thead>
                <tbody data-id="element-2177" data-mp="29oy6umi5fx1f">
                  <tr data-id="element-2178" data-mp="2el5goqdo1son">
                    <td className="border border-black p-2 font-medium" data-id="element-2179" data-mp="2ekm0be3nzccq">
                      {selectedEncounter.vitals?.temp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2180" data-mp="2nmd062biwoyo">
                      {selectedEncounter.vitals?.pulse || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2181" data-mp="1qxnhwavzhuc6">
                      {selectedEncounter.vitals?.resp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2182" data-mp="1zw92scoxhlg4">
                      {selectedEncounter.vitals?.bp || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2183" data-mp="24ploadyb9c4n">
                      {selectedEncounter.vitals?.sp02 || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2184" data-mp="1c64ys47tvjol">
                      {selectedEncounter.vitals?.weight || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2185" data-mp="o3i0n31vub68">
                      {selectedEncounter.vitals?.height || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2186" data-mp="12tpcgzxdwvxy">
                      {selectedEncounter.vitals?.bmi || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2187" data-mp="1bsjdr3lnuahu">
                      {selectedEncounter.vitals?.painScore || ''}
                    </td>
                    <td className="border border-black p-2 font-medium" data-id="element-2188" data-mp="16wc7f9pcqwzd">
                      {selectedEncounter.vitals?.avpu || ''}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ═══ STAT DOSES ADMINISTERED BY NURSING ═══ */}
            {selectedEncounter.statDoses && selectedEncounter.statDoses.length > 0 && <div className="border-2 border-black mb-6" data-id="element-2189" data-mp="1y1hsl49akzcz">
                  <h3 className="font-bold uppercase bg-yellow-100 p-2 border-b border-black text-center text-sm" data-id="element-2190" data-mp="1y29rany6rd7h">
                    ⚡ Stat Doses Administered by Nursing
                  </h3>
                  <table className="w-full border-collapse text-sm" data-id="element-2191" data-mp="1ob5adioy9ia0">
                    <thead data-id="element-2192" data-mp="1t9zssjj094tl">
                      <tr className="bg-gray-100" data-id="element-2193" data-mp="1t96ve95xrro4">
                        <th className="border border-black p-1.5 w-10" data-id="element-2194" data-mp="1fagxwsf7fpz8">No.</th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2195" data-mp="1jclp7ki6kaip">
                          Drug Name
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2196" data-mp="15dufeaacpb0d">
                          Dose
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2197" data-mp="1adzxozuqdl5s">
                          Route
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2198" data-mp="34qgdar9f2bnb">
                          Time Given
                        </th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2199" data-mp="33aca3iyi55nf">
                          Nurse
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2200" data-mp="2tbd11z9tr7zw">
                      {selectedEncounter.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-2201" data-mp="2od1qfmbpgtgm" data-mpi={idx}>
                          <td className="border border-black p-1.5 text-center" data-id="element-2202" data-mp="3lz94rnxta066">
                            {idx + 1}
                          </td>
                          <td className="border border-black p-1.5 font-medium" data-id="element-2203" data-mp="3h1h2q13gcxo9">
                            {dose.drugName}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-2204" data-mp="37amfategzs18">
                            {dose.dose}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-2205" data-mp="8l4eeyd6qe6w">
                            {dose.route}
                          </td>
                          <td className="border border-black p-1.5" data-id="element-2206" data-mp="3rt58qkwhlbt">
                            {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                          </td>
                          <td className="border border-black p-1.5 italic text-xs" data-id="element-2207" data-mp="l1x0vo6nkjmb">
                            {dose.nurseName || ''}
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}

            {/* ═══ CLINICAL NOTES ═══ */}
            <div className="space-y-5 mb-6 text-sm" data-id="element-2208" data-mp="z0d95jk5yi6k">
              {/* PRESENTING COMPLAINT */}
              <div className="border-2 border-black" data-id="element-2209" data-mp="13vslg72iw60v">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2210" data-mp="18r7venxeths0">
                  Presenting Complaint
                </h3>
                <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.presentingComplaint} onChange={e => setNotes({
                ...notes,
                presentingComplaint: e.target.value
              })} data-id="element-2211" data-mp="1iqqe711l2xih" />
              </div>

              {/* HISTORY OF PRESENTING COMPLAINT */}
              <div className="border-2 border-black" data-id="element-2212" data-mp="28163ntzbqr4y">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2213" data-mp="1kcwp9d4fx5nu">
                  History of Presenting Complaint
                </h3>
                <textarea className="w-full p-3 min-h-[100px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.historyOfPresentingComplaint} onChange={e => setNotes({
                ...notes,
                historyOfPresentingComplaint: e.target.value
              })} data-id="element-2214" data-mp="1ga9t500qkyku" />
              </div>

              {/* PAST MEDICAL HISTORY */}
              <div className="border-2 border-black" data-id="element-2215" data-mp="389ckswef148i">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2216" data-mp="3d37cam09ini5">
                  Past Medical History / General Co-Morbidities
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2217" data-mp="349c2h0o748w6">
                  <tbody data-id="element-2218" data-mp="347hmm0rs76yy">
                    {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => {
                    const checked = notes.pastMedicalHistory?.includes(condition) || false;
                    return <tr key={condition} className="border-b border-gray-300" data-id="element-2219" data-mp="2blptfcw78c1x">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2220" data-mp="1tvv58pvawg67">
                            {condition}
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-2221" data-mp="1k0tig0ohzq6s">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-2222" data-mp="1oy3alo39ufyb">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={checked} onChange={e => {
                            const current = notes.pastMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                pastMedicalHistory: [...current, condition]
                              });
                            } else {
                              setNotes({
                                ...notes,
                                pastMedicalHistory: current.filter(c => c !== condition)
                              });
                            }
                          }} data-id="element-2223" data-mp="2co6325b3lnxg" />
                              {checked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-2224" data-mp="az05571ae7ti">
                            <input type="text" placeholder="Notes (optional)" disabled={!checked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.pastMedicalHistoryNotes?.[condition] || ''} onChange={e => setNotes({
                          ...notes,
                          pastMedicalHistoryNotes: {
                            ...(notes.pastMedicalHistoryNotes || {}),
                            [condition]: e.target.value
                          }
                        })} data-id="element-2225" data-mp="ax701w6bnvnm" />
                          </td>
                        </tr>;
                  })}
                  </tbody>
                </table>
              </div>

              {/* DRUG HISTORY */}
              <div className="border-2 border-black" data-id="element-2226" data-mp="1egwu7cx5p830">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2227" data-mp="2yvtdq51dh30y">
                  Drug History
                </h3>
                <textarea className="w-full p-3 min-h-[60px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.drugHistory} onChange={e => setNotes({
                ...notes,
                drugHistory: e.target.value
              })} data-id="element-2228" data-mp="2tzuroo3inx3p" />
                <div className="p-3 border-t border-gray-300 bg-gray-50" data-id="element-2229" data-mp="2b5z1npovnihr">
                  <span className="font-semibold" data-id="element-2230" data-mp="268ytn98gw5o0">Any known allergy:</span>{' '}
                  <span className="text-red-600 font-medium" data-id="element-2231" data-mp="219w0on5rhpwe">
                    {patient.allergies}
                  </span>
                </div>
              </div>

              {/* FAMILY MEDICAL HISTORY */}
              <div className="border-2 border-black" data-id="element-2232" data-mp="3vo6qoyy3eq3r">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2233" data-mp="7ifgzsveaoon">
                  Family Medical History
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2234" data-mp="ch09150hu220">
                  <tbody data-id="element-2235" data-mp="3tqf535etcytj">
                    {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => {
                    const checked = notes.familyMedicalHistory?.includes(condition) || false;
                    return <tr key={condition} className="border-b border-gray-300" data-id="element-2236" data-mp="3fxfkwq436gfn">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2237" data-mp="3601jnxqqhy7x">
                            {condition}
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-2238" data-mp="2ncak5cuqr4yi">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-2239" data-mp="2idz9x10jasfr">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={checked} onChange={e => {
                            const current = notes.familyMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: [...current, condition]
                              });
                            } else {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: current.filter(c => c !== condition)
                              });
                            }
                          }} data-id="element-2240" data-mp="2wd8ef3fdny3w" />
                              {checked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-2241" data-mp="35g0ucut8ghcu">
                            <input type="text" placeholder="Notes (e.g. relative, severity)" disabled={!checked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.familyMedicalHistoryNotes?.[condition] || ''} onChange={e => setNotes({
                          ...notes,
                          familyMedicalHistoryNotes: {
                            ...(notes.familyMedicalHistoryNotes || {}),
                            [condition]: e.target.value
                          }
                        })} data-id="element-2242" data-mp="39fjbdfhxo8ae" />
                          </td>
                        </tr>;
                  })}
                    {(() => {
                    const otherChecked = notes.familyMedicalHistory?.includes('OTHER') || false;
                    return <tr className="border-b border-gray-300" data-id="element-2243" data-mp="63cryxwsmlap">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2244" data-mp="254yu1xtjn6ct">
                            OTHER
                          </td>
                          <td className="border-r border-gray-300 p-2 text-center w-24" data-id="element-2245" data-mp="1w5bp521lvif0">
                            <label className="flex items-center justify-center cursor-pointer" data-id="element-2246" data-mp="1w7ogg5t9754q">
                              <input type="checkbox" className="mr-2 h-4 w-4" checked={otherChecked} onChange={e => {
                            const current = notes.familyMedicalHistory || [];
                            if (e.target.checked) {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: [...current, 'OTHER']
                              });
                            } else {
                              setNotes({
                                ...notes,
                                familyMedicalHistory: current.filter(c => c !== 'OTHER')
                              });
                            }
                          }} data-id="element-2247" data-mp="2169ga44lfmsn" />
                              {otherChecked ? 'Yes' : ''}
                            </label>
                          </td>
                          <td className="p-2" data-id="element-2248" data-mp="37yck0712zdya">
                            <input type="text" placeholder="Specify (e.g. condition, relative)" disabled={!otherChecked} className="w-full p-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-100 disabled:text-gray-400" value={notes.familyMedicalHistoryOther || ''} onChange={e => setNotes({
                          ...notes,
                          familyMedicalHistoryOther: e.target.value
                        })} data-id="element-2249" data-mp="380ozmdjd9c86" />
                          </td>
                        </tr>;
                  })()}
                  </tbody>
                </table>
              </div>

              {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
              <div className="border-2 border-black" data-id="element-2250" data-mp="3enk7rekhhnna">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2251" data-mp="3eoc4ispthrf5">
                  Obstetrics and Gynaecology History
                </h3>
                <textarea className="w-full p-3 min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.obgynHistory} onChange={e => setNotes({
                ...notes,
                obgynHistory: e.target.value
              })} data-id="element-2252" data-mp="3h8i7oy6lxdun" />
              </div>

              {/* SOCIAL HISTORY */}
              <div className="border-2 border-black" data-id="element-2253" data-mp="40x80tvg8yxh">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2254" data-mp="9002xlhptorq">
                  Social History
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-2255" data-mp="dtcsbxu7xzlj">
                  <tbody data-id="element-2256" data-mp="ip1k3ezzyc7a">
                    <tr className="border-b border-gray-300" data-id="element-2257" data-mp="isfmuockonf9">
                      <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-2258" data-mp="mszhnl9obey8">
                        Alcohol
                      </td>
                      <td className="p-2" data-id="element-2259" data-mp="royabl21bj4g">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.alcohol || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          alcohol: e.target.value
                        }
                      })} data-id="element-2260" data-mp="2nte91ahstpac" />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300" data-id="element-2261" data-mp="3bl1pszlcjjyw">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2262" data-mp="1m4kanu59k1y5">
                        Smoking
                      </td>
                      <td className="p-2" data-id="element-2263" data-mp="1h70qkdlvwuya">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.smoking || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          smoking: e.target.value
                        }
                      })} data-id="element-2264" data-mp="3st2muorvrgt2" />
                      </td>
                    </tr>
                    <tr className="border-b border-gray-300" data-id="element-2265" data-mp="352ymcq9znx6a">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2266" data-mp="355kuos4pegpk">
                        Occupation
                      </td>
                      <td className="p-2" data-id="element-2267" data-mp="13cerpc66uwx4">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.occupation || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          occupation: e.target.value
                        }
                      })} data-id="element-2268" data-mp="13bc0t48v8uh7" />
                      </td>
                    </tr>
                    <tr data-id="element-2269" data-mp="3d34p2cbuumic">
                      <td className="border-r border-gray-300 p-2 font-medium" data-id="element-2270" data-mp="3d3w7elgfynqo">
                        Mental Status
                      </td>
                      <td className="p-2" data-id="element-2271" data-mp="3838gs0w4k8wz">
                        <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.socialHistory?.mentalStatus || ''} onChange={e => setNotes({
                        ...notes,
                        socialHistory: {
                          ...notes.socialHistory,
                          mentalStatus: e.target.value
                        }
                      })} data-id="element-2272" data-mp="341wqdt5ffjnq" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* REVIEW OF SYSTEMS */}
              <div className="border-2 border-black" data-id="element-2273" data-mp="1emvrxffyb21o">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2274" data-mp="h66qvnyts3tc">
                  Review of Systems
                </h3>
                <div className="p-3 space-y-2" data-id="element-2275" data-mp="m35j20yxi0ow">
                  {[{
                  key: 'rs',
                  label: 'R/S:'
                }, {
                  key: 'cvs',
                  label: 'CVS:'
                }, {
                  key: 'cns',
                  label: 'CNS:'
                }, {
                  key: 'git',
                  label: 'GIT:'
                }, {
                  key: 'gut',
                  label: 'GUT:'
                }, {
                  key: 'mss',
                  label: 'MSS:'
                }, {
                  key: 'is',
                  label: 'IS:'
                }].map(system => <div key={system.key} className="flex items-center" data-id="element-2276" data-mp="1gklyv8untjfa">
                      <span className="font-semibold w-16" data-id="element-2277" data-mp="1bjg83gkk868e">{system.label}</span>
                      <input type="text" className="flex-1 p-1 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={notes.reviewOfSystems?.[system.key as keyof typeof notes.reviewOfSystems] || ''} onChange={e => setNotes({
                    ...notes,
                    reviewOfSystems: {
                      ...notes.reviewOfSystems,
                      [system.key]: e.target.value
                    }
                  })} data-id="element-2278" data-mp="2tgjmqekn6nxz" />
                    </div>)}
                </div>
              </div>

              {/* ON EXAMINATION */}
              <div className="border-2 border-black" data-id="element-2279" data-mp="4rldqvkzl297">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2280" data-mp="20swzay5b52ca">
                  On Examination
                </h3>
                <div className="p-4 space-y-3" data-id="element-2281" data-mp="1vzu1gdthz6qa">
                  {[{
                  key: 'gc' as const,
                  label: 'G/C',
                  helper: 'Stable / Ill Looking / Unconscious'
                }, {
                  key: 'febrile' as const,
                  label: 'Temperature',
                  helper: 'Febrile / Afebrile / Lethargic'
                }, {
                  key: 'pjc' as const,
                  label: 'P | J | C',
                  helper: 'Pallor / Jaundice / Cyanosis'
                }, {
                  key: 's1s2' as const,
                  label: 'S1  S2',
                  helper: 'Heart sounds'
                }, {
                  key: 'rs' as const,
                  label: 'R/S',
                  helper: 'Respiratory system'
                }, {
                  key: 'pa' as const,
                  label: 'P/A',
                  helper: 'Per abdomen'
                }].map(field => <div key={field.key} className="grid grid-cols-[7rem_1fr] gap-3 items-start" data-id="element-2282" data-mp="3jt5y9f8s11ro">
                      <div className="pt-2" data-id="element-2283" data-mp="3to6b2hlyf60t">
                        <p className="font-bold text-sm text-gray-900" data-id="element-2284" data-mp="1los951g8jbs">
                          {field.label}
                        </p>
                        <p className="text-xs text-gray-500 leading-tight" data-id="element-2285" data-mp="6jqd9vifqspw">
                          {field.helper}
                        </p>
                      </div>
                      <input type="text" value={notes.onExaminationDetails?.[field.key] || ''} onChange={e => setNotes({
                    ...notes,
                    onExaminationDetails: {
                      ...(notes.onExaminationDetails || {
                        gc: '',
                        febrile: '',
                        pjc: '',
                        s1s2: '',
                        rs: '',
                        pa: ''
                      }),
                      [field.key]: e.target.value
                    }
                  })} placeholder={`Enter ${field.label} findings`} className="w-full border-2 border-gray-300 rounded-md p-2 text-sm bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none" data-id="element-2286" data-mp="28bmmuzl1lsi1" />
                    </div>)}

                  {/* Additional notes (free text) */}
                  <div className="pt-3 border-t border-gray-200" data-id="element-2287" data-mp="38r9o3krti4ck">
                    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wide" data-id="element-2288" data-mp="33x5f6vyzrm8u">
                      Additional Notes
                    </label>
                    <textarea className="w-full p-2 min-h-[80px] text-sm border-2 border-gray-300 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none" placeholder="Any other examination findings..." value={notes.onExamination} onChange={e => setNotes({
                    ...notes,
                    onExamination: e.target.value
                  })} data-id="element-2289" data-mp="2uythk9zpjo6w" />
                  </div>
                </div>
              </div>

              {/* SUMMARY OF SIGNIFICANT FINDINGS */}
              <div className="border-2 border-black" data-id="element-2290" data-mp="23q0c86w7qbr8">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2291" data-mp="23offqbqc8iqq">
                  Summary of Significant Findings
                </h3>
                <div className="p-3 space-y-2" data-id="element-2292" data-mp="1uosdxotp5ax5">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <div key={i} className="flex items-start" data-id="element-2293" data-mp="1znlr1g52mzws">
                      <span className="font-medium mr-2 mt-1" data-id="element-2294" data-mp="1l0ag9ggprjxq">{i + 1}.</span>
                      <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.significantFindings?.[i] || ''} onChange={e => {
                    const current = notes.significantFindings || Array(8).fill('');
                    const updated = [...current];
                    updated[i] = e.target.value;
                    setNotes({
                      ...notes,
                      significantFindings: updated
                    });
                  }} data-id="element-2295" data-mp="1ky7fc1e0w6e8" />
                    </div>)}
                </div>
              </div>

              {/* WORKING DIAGNOSIS */}
              <div className="border-2 border-black" data-id="element-2296" data-mp="scr8cvzez5so">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-2297" data-mp="16dbr5s5bx1m6">
                  Working Diagnosis
                </h3>
                <div className="p-3 space-y-2" data-id="element-2298" data-mp="11dh27u546m3r">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9].map(i => <div key={i} className="flex items-start" data-id="element-2299" data-mp="1g2d75ibvjj05">
                      <span className="font-medium mr-2 mt-1" data-id="element-2300" data-mp="1b3s65gossvan">{i + 1}.</span>
                      <input type="text" className="flex-1 p-1 border-b border-gray-300 focus:border-blue-500 focus:outline-none" value={notes.workingDiagnosis?.[i] || ''} onChange={e => {
                    const current = notes.workingDiagnosis || Array(10).fill('');
                    const updated = [...current];
                    updated[i] = e.target.value;
                    setNotes({
                      ...notes,
                      workingDiagnosis: updated
                    });
                  }} data-id="element-2301" data-mp="1p3jp7vu691vi" />
                    </div>)}
                </div>
              </div>

              {/* INVESTIGATIONS TO BE DONE */}
              <div className="border-2 border-black" data-id="element-2302" data-mp="35iyyzibxhdih">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2303" data-mp="39mdi4hv5ya05">
                  Investigations To Be Done
                </h3>
                <div className="p-4" data-id="element-2304" data-mp="kwdvcmn3xnpy">
                  {/* LABORATORY TESTS */}
                  <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-2305" data-mp="ottksiv0bdgo">
                    Laboratory Tests
                  </h4>
                  <table className="w-full border-collapse text-sm mb-4" data-id="element-2306" data-mp="25btvms58m10c">
                    <tbody data-id="element-2307" data-mp="20l3q9ewt9y5n">
                      {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATININE / U&E', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['HORMONAL PROFILE', 'ANTI-MÜLLERIAN HORMONE'], ['RPR', 'HIV'], ['SPERM ANALYSIS', 'TESTOSTERONE'], ['DOA 6', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-2308" data-mp="3q62dhj1s3aye" data-mpi={idx}>
                          {pair.map((test, ci) => {
                        if (!test) {
                          return <Fragment key={`empty-${ci}`} data-id="element-2309" data-mp="3h5w5gcoun36m">
                                  <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-2310" data-mp="3h39r9u7gdpff"></td>
                                  <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-2311" data-mp="3m2cv5bbp5plt"></td>
                                </Fragment>;
                        }
                        const isOrdered = selectedEncounter.labRequests.some(lab => lab.tests.some(t => t.toLowerCase().includes(test.toLowerCase()) || test.toLowerCase().includes(t.toLowerCase())));
                        return <Fragment key={test} data-id="element-2312" data-mp="2uwp4dtj7fwnm" data-mpi={ci}>
                                <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-2313" data-mp="2uwfee5y5tphx">
                                  {test}
                                </td>
                                <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-2314" data-mp="229bf8hlr05e2">
                                  {isOrdered ? <span className="text-green-700 font-bold" data-id="element-2315" data-mp="1y4vbalvp381h">
                                      ✓
                                    </span> : ''}
                                </td>
                              </Fragment>;
                      })}
                        </tr>)}
                    </tbody>
                  </table>

                  {/* RADIOLOGY SCANS */}
                  <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-2316" data-mp="xz8ojfzd7r89">
                    Radiology Scans
                  </h4>
                  <table className="w-full border-collapse text-sm mb-4" data-id="element-2317" data-mp="17wn134i63vva">
                    <tbody data-id="element-2318" data-mp="17uk5m4y8xaj6">
                      {[['PELVIC SCAN', 'ANOMALY SCAN'], ['OBS SCAN', 'CXR (CHEST X-RAY)'], ['HSG (HYSTEROSALPINGOGRAPHY)', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-2319" data-mp="1lol5j20xp3l4" data-mpi={idx}>
                          {pair.map((scan, ci) => {
                        if (!scan) {
                          return <Fragment key={`empty-r-${ci}`} data-id="element-2320" data-mp="1vk4oknueg7sg">
                                  <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-2321" data-mp="20mdjvquqvlxq"></td>
                                  <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-2322" data-mp="3l4ebvsjgkys5"></td>
                                </Fragment>;
                        }
                        const isOrdered = selectedEncounter.radiologyRequests.some(r => {
                          const a = r.scanType.toLowerCase();
                          const b = scan.toLowerCase();
                          return a.includes(b) || b.includes(a) || b.includes('cxr') && a.includes('cxr') || b.includes('hsg') && a.includes('hsg');
                        });
                        return <Fragment key={scan} data-id="element-2323" data-mp="qh0z6gpurpd2" data-mpi={ci}>
                                <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-2324" data-mp="ln6lpv0uc6mf">
                                  {scan}
                                </td>
                                <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-2325" data-mp="1iejuo3awq32q">
                                  {isOrdered ? <span className="text-green-700 font-bold" data-id="element-2326" data-mp="1wf4en2sk64xt">
                                      ✓
                                    </span> : ''}
                                </td>
                              </Fragment>;
                      })}
                        </tr>)}
                    </tbody>
                  </table>

                  {/* ─── PENDING INVESTIGATIONS — RECALL ─── */}
                  {(() => {
                  const pendingLabs = selectedEncounter.labRequests.filter(l => l.status === 'Pending');
                  const pendingRads = selectedEncounter.radiologyRequests.filter(r => r.status === 'Pending');
                  const hasAny = pendingLabs.some(l => l.tests.length > 0 || l.otherTests.trim().length) || pendingRads.length > 0;
                  if (!hasAny) return null;
                  return <div className="mb-4 border border-amber-300 bg-amber-50 rounded-md p-3" data-id="element-2327" data-mp="2ppecymcnjv6b">
                        <h4 className="font-bold text-xs uppercase text-amber-800 mb-2" data-id="element-2328" data-mp="2b0i8ez9t10ag">
                          Pending Investigations (Recall to make changes)
                        </h4>
                        <ul className="space-y-1.5 text-sm" data-id="element-2329" data-mp="2fx8mxi5ynntr">
                          {pendingLabs.flatMap(lab => lab.tests.map(testName => <li key={`${lab.id}-${testName}`} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-2330" data-mp="26y3o0aqhlsh2">
                                <span data-id="element-2331" data-mp="2s3nz5ylfmkx">
                                  <span className="font-medium" data-id="element-2332" data-mp="2t4x34w1tz00">
                                    {testName}
                                  </span>{' '}
                                  <span className="text-xs text-gray-500" data-id="element-2333" data-mp="vhsozjbazmmj">
                                    · Laboratory ·{' '}
                                    {new Date(lab.requestedAt).toLocaleTimeString()}
                                  </span>
                                </span>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallLabTest(lab.id, testName)} data-id="element-2334" data-mp="3773gkrfhfj93">
                                  <RotateCcw className="w-3 h-3 mr-1" data-id="element-2335" data-mp="1qqe3o1c0n98m" /> Recall
                                </Button>
                              </li>))}
                          {pendingLabs.filter(l => l.otherTests.trim().length > 0).map(lab => <li key={`${lab.id}-other`} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-2336" data-mp="2aen5z479wvlu">
                                <span data-id="element-2337" data-mp="g26u9nvpkp9l">
                                  <span className="font-medium" data-id="element-2338" data-mp="k27ke5s66owh">
                                    {lab.otherTests}
                                  </span>{' '}
                                  <span className="text-xs text-gray-500" data-id="element-2339" data-mp="3kvu593b298jw">
                                    · Other lab test
                                  </span>
                                </span>
                                <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallLabTest(lab.id, lab.otherTests, true)} data-id="element-2340" data-mp="3uutf2p7kba8h">
                                  <RotateCcw className="w-3 h-3 mr-1" data-id="element-2341" data-mp="2jeuxty28dhw6" /> Recall
                                </Button>
                              </li>)}
                          {pendingRads.map(rad => <li key={rad.id} className="flex items-center justify-between bg-white border border-amber-200 rounded px-2 py-1" data-id="element-2342" data-mp="yq0pxpqtuuv0">
                              <span data-id="element-2343" data-mp="p1929tssuwpw">
                                <span className="font-medium" data-id="element-2344" data-mp="oy4qobd08yp7">
                                  {rad.scanType}
                                </span>{' '}
                                <span className="text-xs text-gray-500" data-id="element-2345" data-mp="1hp4xsv6muil3">
                                  · Radiology ·{' '}
                                  {new Date(rad.requestedAt).toLocaleTimeString()}
                                </span>
                              </span>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-400 hover:bg-red-50 text-xs" onClick={() => handleRecallRadiology(rad.id)} data-id="element-2346" data-mp="3ohp6773pn3i0">
                                <RotateCcw className="w-3 h-3 mr-1" data-id="element-2347" data-mp="1p7matbdpwfit" /> Recall
                              </Button>
                            </li>)}
                        </ul>
                        <p className="text-xs text-amber-700 mt-2 italic" data-id="element-2348" data-mp="1g7zh7br8ce0b">
                          Recall removes the request before the lab or radiology
                          department processes it. Once a test is completed, it
                          becomes part of the permanent record.
                        </p>
                      </div>;
                })()}

                  <div className="flex gap-3 mb-4" data-id="element-2349" data-mp="30nugvmscy8z8">
                    <Button variant="outline" onClick={() => setShowLabModal(true)} className="flex-1" data-id="element-2350" data-mp="30l89bn5gvth0">
                      <FlaskConical className="w-4 h-4 mr-2" data-id="element-2351" data-mp="ic3z0plz4fl9" /> Request
                      Laboratory Test
                    </Button>
                    <Button variant="outline" onClick={() => setShowRadModal(true)} className="flex-1 border-purple-600 text-purple-600 hover:bg-purple-50" data-id="element-2352" data-mp="s3hrwejf6h5f">
                      <Scan className="w-4 h-4 mr-2" data-id="element-2353" data-mp="3vmb34koq6twf" /> Request Radiology Scan
                    </Button>
                  </div>

                  <div className="border-t border-gray-300 pt-2" data-id="element-2354" data-mp="4g9vyn7vskg2">
                    <span className="font-semibold" data-id="element-2355" data-mp="8eh62z7uania">Other investigations:</span>{' '}
                    {selectedEncounter.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                  </div>

                  {/* Investigation Results — View File per test */}
                  {(selectedEncounter.labRequests.some(l => l.status === 'Completed') || selectedEncounter.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-2356" data-mp="14jbpiqcof8m3">
                      <h4 className="font-bold mb-3 uppercase text-center" data-id="element-2357" data-mp="14i0mwtuyw6wk">
                        Investigation Results
                      </h4>
                      <table className="w-full border-collapse text-sm" data-id="element-2358" data-mp="1e9mv0hc0dpv3">
                        <thead data-id="element-2359" data-mp="1s8w99bgnm9wf">
                          <tr className="bg-gray-100" data-id="element-2360" data-mp="1s962d7wzwx6c">
                            <th className="border border-black p-1.5 text-left" data-id="element-2361" data-mp="1s6jvzblucnpp">
                              Test / Scan
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2362" data-mp="3u10xocyy2p2e">
                              Type
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2363" data-mp="3fupj8mux58oi">
                              Performed By
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-2364" data-mp="3jv1522z0pf1k">
                              Completed
                            </th>
                            <th className="border border-black p-1.5 text-center w-32" data-id="element-2365" data-mp="3b3r0lgmgjyi3">
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-2366" data-mp="316352oh12myv">
                          {selectedEncounter.labRequests.filter(l => l.status === 'Completed').flatMap(lab => lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).map(testName => <tr key={`${lab.id}-${testName}`} data-id="element-2367" data-mp="ei0gbrj4veeq">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-2368" data-mp="jhdayyg8xprv">
                                      {testName}
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-2369" data-mp="5nvqnbfqk90s">
                                      Laboratory
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-2370" data-mp="3rweuby1c0vgj">
                                      —
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2371" data-mp="3iwrx7ofmybta">
                                      {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center" data-id="element-2372" data-mp="35o9h6jxsy5wo">
                                      <div className="flex justify-center gap-2" data-id="element-2373" data-mp="31nfv7w3x716f">
                                        <Button size="sm" variant="outline" onClick={() => setViewingTest({
                              kind: 'lab',
                              labId: lab.id,
                              testName
                            })} data-id="element-2374" data-mp="3fekma9t82l55">
                                          <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2375" data-mp="3tc9b1sqeucfh" />{' '}
                                          View File
                                        </Button>
                                        <button onClick={() => setRemoveTestModal({
                              isOpen: true,
                              labId: lab.id,
                              testName,
                              reason: ''
                            })} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors print:hidden" title="Remove duplicate test" data-id="element-2376" data-mp="1z25keyfwgwki">
                                          <Trash2 className="w-4 h-4" data-id="element-2377" data-mp="10l7xksp3g8mu" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>))}
                          {selectedEncounter.labRequests.filter(l => l.status === 'Completed' && l.otherTests && !(l.removedTestNames || []).includes(l.otherTests)).map(lab => <tr key={`${lab.id}-other`} data-id="element-2378" data-mp="38sjtulud9y05">
                                <td className="border border-black p-1.5 font-medium" data-id="element-2379" data-mp="38t3dc6zo0uga">
                                  {lab.otherTests}{' '}
                                  <span className="text-xs text-gray-500" data-id="element-2380" data-mp="2z2rvrhn26mqx">
                                    (Other)
                                  </span>
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2381" data-mp="3rrfofya50eei">
                                  Laboratory
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2382" data-mp="3bad8ine6ivna">—</td>
                                <td className="border border-black p-1.5 text-xs" data-id="element-2383" data-mp="3gc2jy45bdbbw">
                                  {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                </td>
                                <td className="border border-black p-1.5 text-center" data-id="element-2384" data-mp="3uznq66xhjqo1">
                                  <div className="flex justify-center gap-2" data-id="element-2385" data-mp="2wmpter8shli">
                                    <Button size="sm" variant="outline" onClick={() => setViewingTest({
                              kind: 'lab',
                              labId: lab.id,
                              testName: lab.otherTests
                            })} data-id="element-2386" data-mp="3la430p2qpand">
                                      <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2387" data-mp="rwssu9ln8hvm" /> View
                                      File
                                    </Button>
                                    <button onClick={() => setRemoveTestModal({
                              isOpen: true,
                              labId: lab.id,
                              testName: lab.otherTests,
                              reason: ''
                            })} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors print:hidden" title="Remove duplicate test" data-id="element-2388" data-mp="10pcnkyyte2b6">
                                      <Trash2 className="w-4 h-4" data-id="element-2389" data-mp="21eg47jukpigi" />
                                    </button>
                                  </div>
                                </td>
                              </tr>)}
                          {selectedEncounter.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <tr key={rad.id} data-id="element-2390" data-mp="1hp5ow6yqozix">
                                <td className="border border-black p-1.5 font-medium" data-id="element-2391" data-mp="1mr4ttla4o4hz">
                                  {rad.scanType}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2392" data-mp="1wfmqprfadrgc">
                                  Radiology
                                </td>
                                <td className="border border-black p-1.5" data-id="element-2393" data-mp="z08tfzrlw0z4">
                                  {rad.radiographerName || '—'}
                                </td>
                                <td className="border border-black p-1.5 text-xs" data-id="element-2394" data-mp="wf1bpfuld9x5">
                                  {rad.completedAt ? new Date(rad.completedAt).toLocaleString() : ''}
                                </td>
                                <td className="border border-black p-1.5 text-center" data-id="element-2395" data-mp="hhs8novxehpl">
                                  <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'rad',
                            radId: rad.id
                          })} data-id="element-2396" data-mp="dhgo0c3kche4">
                                    <Eye className="w-3.5 h-3.5 mr-1" data-id="element-2397" data-mp="3lx0cyyx7bdnd" /> View
                                    File
                                  </Button>
                                </td>
                              </tr>)}
                        </tbody>
                      </table>
                    </div>}
                </div>
              </div>

              {/* DRUG TREATMENT / PRESCRIPTION */}
              <div className="border-2 border-black" data-id="element-2398" data-mp="18no1uwq7ahee">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2399" data-mp="uy4465bh8aby">
                  Drug Treatment / Prescription
                  {selectedEncounter.prescriptions.length > 0 && <span className="ml-2 text-xs font-normal text-gray-600 lowercase" data-id="element-2400" data-mp="l0g02orzdqh4">
                      ({selectedEncounter.prescriptions.length} of 20 drugs)
                    </span>}
                </h3>
                <div className="max-h-[420px] overflow-y-auto" data-id="element-2401" data-mp="bzrumignkyfu">
                  <table className="w-full border-collapse text-sm text-center" data-id="element-2402" data-mp="g0tlaawi8267">
                    <thead data-id="element-2403" data-mp="3th5imr9aefxl">
                      <tr className="bg-gray-100" data-id="element-2404" data-mp="3tigm0pytllo6">
                        <th className="border border-black p-1.5 w-10" data-id="element-2405" data-mp="3oikieru1bw95">No.</th>
                        <th className="border border-black p-1.5 text-left" data-id="element-2406" data-mp="3oknik4owj5rl">
                          Drug Name
                        </th>
                        <th className="border border-black p-1.5" data-id="element-2407" data-mp="1dd1hue3vdou">Dose</th>
                        <th className="border border-black p-1.5" data-id="element-2408" data-mp="gb5h0mjnk231">Route</th>
                        <th className="border border-black p-1.5" data-id="element-2409" data-mp="g9ar0ttixjs3">Frequency</th>
                        <th className="border border-black p-1.5" data-id="element-2410" data-mp="bbj30aw02ht6">
                          No. of Days
                        </th>
                        <th className="border border-black p-1.5" data-id="element-2411" data-mp="p5a0gkwgib8j">Signature</th>
                        <th className="border border-black p-1.5 w-16" data-id="element-2412" data-mp="l2nf92uajokb">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-2413" data-mp="u43i8pis0oei">
                      {selectedEncounter.prescriptions.length > 0 ? selectedEncounter.prescriptions.map((rx, i) => <tr key={rx.id} className={rx.status === 'Dispensed' ? 'bg-green-50' : rx.status === 'Prescribed' || rx.status === 'Not Dispensed' ? 'bg-amber-50' : ''} data-id="element-2414" data-mp="ij31eifrb8up" data-mpi={i}>
                              <td className="border border-black p-1.5" data-id="element-2415" data-mp="3qymr57h7y950">
                                {i + 1}
                              </td>
                              <td className="border border-black p-1.5 text-left font-medium" data-id="element-2416" data-mp="3gznpi8uvfdri">
                                {rx.drugName}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2417" data-mp="3c2nk805uif1g">
                                {rx.dose}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2418" data-mp="36uxmdznqe3lp">
                                {rx.route}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2419" data-mp="3bsqoohlwydgp">
                                {rx.frequency}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2420" data-mp="2j77bhuhr33z3">
                                {rx.days}
                              </td>
                              <td className="border border-black p-1.5 text-xs" data-id="element-2421" data-mp="2o2wde4h8swyk">
                                {rx.status === 'Dispensed' && <span className="text-green-700 font-bold" data-id="element-2422" data-mp="2xw3sih1sbrbu">
                                    ✓ Dispensed
                                  </span>}
                                {(rx.status === 'Prescribed' || rx.status === 'Not Dispensed') && <span className="font-bold text-amber-700" data-id="element-2423" data-mp="1xc8ljfx1gdzk">
                                    ℞ Prescribed — not in stock
                                  </span>}
                                {rx.status === 'Pending' && <span className="italic text-gray-500" data-id="element-2424" data-mp="22637yr41372c">
                                    Pending
                                  </span>}
                                {rx.pharmacistName && <div className="text-gray-500 italic mt-0.5" data-id="element-2425" data-mp="1od3qwkoll4so">
                                    {rx.pharmacistName}
                                  </div>}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-2426" data-mp="2zr7hrhzw2cti">
                                {rx.status === 'Pending' ? <button onClick={() => handleDeletePrescription(rx.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors" title="Remove this drug" aria-label={`Remove ${rx.drugName}`} data-id="element-2427" data-mp="2uvs70yx6gx1b">
                                    <Trash2 className="w-4 h-4" data-id="element-2428" data-mp="3l5sm9jqgq400" />
                                  </button> : <span className="text-gray-300" data-id="element-2429" data-mp="3cbxpp03eplvz">—</span>}
                              </td>
                            </tr>) : Array.from({
                      length: 20
                    }, (_, idx) => idx + 1).map(i => <tr key={i} data-id="element-2430" data-mp="2l60jdwk7mkco">
                              <td className="border border-black p-3" data-id="element-2431" data-mp="2g5vzrxo099i8">{i}</td>
                              <td className="border border-black p-3" data-id="element-2432" data-mp="2g7z0bbmsal17"></td>
                              <td className="border border-black p-3" data-id="element-2433" data-mp="2b6t7ldt4ixro"></td>
                              <td className="border border-black p-3" data-id="element-2434" data-mp="275ziwi7uxexq"></td>
                              <td className="border border-black p-3" data-id="element-2435" data-mp="33vqe6jnz364l"></td>
                              <td className="border border-black p-3" data-id="element-2436" data-mp="33x1jwp0zanyc"></td>
                              <td className="border border-black p-3" data-id="element-2437" data-mp="2ywvvdhq09hkh"></td>
                              <td className="border border-black p-3" data-id="element-2438" data-mp="2u42s6ya6qhai"></td>
                            </tr>)}
                    </tbody>
                  </table>
                </div>
                <div className="p-3 border-t border-gray-300 flex gap-3" data-id="element-2439" data-mp="3mmhts2yjzsrf">
                  <Button variant="outline" onClick={() => setShowRxModal(true)} className="flex-1 text-green-600 border-green-600 hover:bg-green-50" disabled={selectedEncounter.prescriptions.length >= 20} data-id="element-2440" data-mp="3huq1nnzr1ez6">
                    <Pill className="w-4 h-4 mr-2" data-id="element-2441" data-mp="1dcqrdtnjcvea" />
                    {selectedEncounter.prescriptions.length >= 20 ? 'Maximum 20 Drugs Reached' : 'Prescribe Medication'}
                  </Button>
                  {selectedEncounter.prescriptions.some(rx => rx.status === 'Pending') && <Button onClick={handleSendToPharmacy} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-id="element-2442" data-mp="1x0hh2pbtpqxf">
                      <Pill className="w-4 h-4 mr-2" data-id="element-2443" data-mp="261pbgwb7y7m8" /> Send Prescription to
                      Pharmacy
                    </Button>}
                </div>
              </div>

              {/* FOLLOW UP / REVIEW DATE */}
              <div className="border-2 border-black" data-id="element-2444" data-mp="azaupivj76ri">
                <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-2445" data-mp="5zg65lz8at9m">
                  Next Follow Up / Review Date (If Any)
                </h3>
                <table className="w-full border-collapse text-sm text-center" data-id="element-2446" data-mp="kqf9oil7irmc">
                  <thead data-id="element-2447" data-mp="fw16hu0azg74">
                    <tr className="bg-gray-100" data-id="element-2448" data-mp="fvsv66jtwiy1">
                      <th className="border border-black p-1 w-10" data-id="element-2449" data-mp="tnze20ed9416">No.</th>
                      <th className="border border-black p-1" data-id="element-2450" data-mp="pnylzcycgubn">Date</th>
                      <th className="border border-black p-1 w-10" data-id="element-2451" data-mp="pprt0tcvdgk6">No.</th>
                      <th className="border border-black p-1" data-id="element-2452" data-mp="13mf4s29heba5">Date</th>
                      <th className="border border-black p-1 w-10" data-id="element-2453" data-mp="yp55zw07cn9n">No.</th>
                      <th className="border border-black p-1" data-id="element-2454" data-mp="35ebhvgbart71">Date</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-2455" data-mp="30gi9qhqfop44">
                    {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-2456" data-mp="1fwwc9vdva8x2">
                        <td className="border border-black p-1 font-medium" data-id="element-2457" data-mp="1fxy070fzepw7">
                          {row + 1}
                        </td>
                        <td className="border border-black p-1" data-id="element-2458" data-mp="167mhu8vjwe5s">
                          {row === 0 ? <input type="date" className="w-full p-1 text-center focus:ring-2 focus:ring-blue-500 focus:outline-none" value={selectedEncounter.reviewDate ? new Date(selectedEncounter.reviewDate).toISOString().split('T')[0] : ''} onChange={e => {
                        if (e.target.value) {
                          updateEncounter(selectedEncounter.id, {
                            reviewDate: new Date(e.target.value).toISOString()
                          });
                        }
                      }} data-id="element-2459" data-mp="1b6g00els8tca" /> : ''}
                        </td>
                        <td className="border border-black p-1 font-medium" data-id="element-2460" data-mp="qpbgxi7q8sxc">
                          {row + 6}
                        </td>
                        <td className="border border-black p-2" data-id="element-2461" data-mp="mpb4220a0pse"></td>
                        <td className="border border-black p-1 font-medium" data-id="element-2462" data-mp="hodlvpf9nvqw">
                          {row + 11}
                        </td>
                        <td className="border border-black p-2" data-id="element-2463" data-mp="2c1mpx1p1mp1r"></td>
                      </tr>)}
                  </tbody>
                </table>
                <div className="border-t border-black p-3" data-id="element-2464" data-mp="273l4ia45adn4">
                  <h4 className="font-bold uppercase text-sm mb-2" data-id="element-2465" data-mp="2en2r5wcorjnw">Notes</h4>
                  <textarea className="w-full p-2 border border-gray-300 rounded text-sm min-h-[80px] focus:ring-2 focus:ring-blue-500 focus:outline-none" placeholder="Add any notes related to the follow-up or review (e.g. reason for review, instructions to patient, expected investigations)…" value={reviewDateNotes} onChange={e => setReviewDateNotes(e.target.value)} data-id="element-2466" data-mp="2jl4e4sd8g34n" />
                </div>
              </div>

              {/* NAME OF MEDICAL PRACTITIONER */}
              <div className="border-2 border-black p-4 mt-6" data-id="element-2467" data-mp="25npjn984h55t">
                <p className="font-bold text-sm" data-id="element-2468" data-mp="2alqz7k6camcj">
                  Name of the Medical Practitioner:{' '}
                  <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-2469" data-mp="3h0048scppuz6">
                    {selectedEncounter.clinicalNote?.clinicianName || currentUser?.fullName || ''}
                  </span>
                </p>
              </div>
            </div>
          </fieldset>

          {/* VERSION HISTORY VIEWER */}
          {selectedEncounter.clinicalNote?.editHistory && selectedEncounter.clinicalNote.editHistory.length > 0 && <div className="mt-8 border-t-2 border-dashed border-gray-300 pt-6 print:hidden" data-id="element-2470" data-mp="1cwae9qp24r3s">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center" data-id="element-2471" data-mp="p66clovfj1ff">
                  <History className="w-5 h-5 mr-2 text-gray-500" data-id="element-2472" data-mp="k8eci7kms905" />
                  Version History
                </h3>
                <div className="space-y-4" data-id="element-2473" data-mp="y4i3zt44e38x">
                  {selectedEncounter.clinicalNote.editHistory.map((edit, idx) => <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4" data-id="element-2474" data-mp="29kq83yz79mmp" data-mpi={idx}>
                        <div className="flex justify-between items-start mb-2" data-id="element-2475" data-mp="2d3ypt5vljdmr">
                          <div data-id="element-2476" data-mp="2d61qqkyaer69">
                            <p className="font-semibold text-gray-900" data-id="element-2477" data-mp="2i31xyz6vhzyy">
                              Edited by {edit.editedByName}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-2478" data-mp="237vryc7s215m">
                              {new Date(edit.editedAt).toLocaleString()}
                            </p>
                          </div>
                          <Badge variant="warning" data-id="element-2479" data-mp="2vwj85tdo60cb">Version {idx + 1}</Badge>
                        </div>
                        <div className="bg-white border border-gray-100 p-3 rounded text-sm text-gray-700 mt-2" data-id="element-2480" data-mp="2m4dd2as4f2sw">
                          <span className="font-semibold text-gray-900" data-id="element-2481" data-mp="2r36qy4bbkvtl">
                            Reason for change:
                          </span>{' '}
                          {edit.reason}
                        </div>
                        <details className="mt-3" data-id="element-2482" data-mp="3jksvizqj6w5k">
                          <summary className="text-sm text-blue-600 cursor-pointer hover:underline font-medium" data-id="element-2483" data-mp="3tfrgskiq61f9">
                            View previous note content
                          </summary>
                          <div className="mt-2 p-3 bg-white border border-gray-200 rounded text-xs text-gray-600 max-h-60 overflow-y-auto font-mono whitespace-pre-wrap" data-id="element-2484" data-mp="3kf1m5k57ah34">
                            {JSON.stringify(edit.previousNote, null, 2)}
                          </div>
                        </details>
                      </div>)}
                </div>
              </div>}
        </div>

        {/* ═══ MODALS ═══ */}
        {removeTestModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-2485" data-mp="2wpqwaxpe273r">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-2486" data-mp="2rqxeit32jtxs">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center text-red-600" data-id="element-2487" data-mp="2mrb1ajn8qdoz">
                <Trash2 className="w-5 h-5 mr-2" data-id="element-2488" data-mp="1298vek1s5uz0" /> Remove Duplicate Test
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-2489" data-mp="178d6jg89m2rz">
                Why are you removing "{removeTestModal.testName}"? This action
                will be logged in the audit trail.
              </p>
              <div className="mb-4" data-id="element-2490" data-mp="1g91j06i0fv2r">
                <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-red-500 focus:outline-none" rows={3} value={removeTestModal.reason} onChange={e => setRemoveTestModal({
              ...removeTestModal,
              reason: e.target.value
            })} placeholder="e.g., Duplicate entry, entered by mistake..." data-id="element-2491" data-mp="1g80ao9sdpmoq" />
              </div>
              <div className="flex justify-end space-x-3" data-id="element-2492" data-mp="4tv3l7wvcwp6">
                <Button variant="outline" onClick={() => setRemoveTestModal({
              isOpen: false,
              labId: '',
              testName: '',
              reason: ''
            })} data-id="element-2493" data-mp="4sbnpif4knnx">
                  Cancel
                </Button>
                <Button variant="danger" onClick={handleRemoveTestConfirm} disabled={!removeTestModal.reason.trim()} data-id="element-2494" data-mp="3msyr7hamj8hv">
                  Confirm Removal
                </Button>
              </div>
            </div>
          </div>}

        {/* Lab Modal */}
        {showLabModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-2495" data-mp="9f3t6lyim5rg">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col" data-id="element-2496" data-mp="2g4s41annq40g">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2497" data-mp="2b6qqf5902wwd">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2498" data-mp="qnwknimwtuab">
                  Request Laboratory Tests
                </h3>
                <button onClick={() => setShowLabModal(false)} data-id="element-2499" data-mp="vmq21m5bi5fr">
                  <X className="w-5 h-5 text-gray-400" data-id="element-2500" data-mp="gz4yjr4j647b" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto flex-1" data-id="element-2501" data-mp="lwoc0otemcy7">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6" data-id="element-2502" data-mp="lv3fwurfyly8">
                  <div data-id="element-2503" data-mp="7w3udvz86n6a">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2504" data-mp="801fgn5d5ost">
                      HAEMATOLOGY
                    </h4>
                    {['FBC', 'Blood Group', 'CD4 Count', 'MPS/RDT', 'AAFB', 'Viral Load'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2505" data-mp="1sbp9iyy2v36w">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2506" data-mp="1vwhe4famy3hp" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2507" data-mp="1zvzsstbv5ic3">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2508" data-mp="1zwrgzj34sdsc">
                      CHEMISTRY
                    </h4>
                    {['Urea', 'Creatinine', 'U&E', 'FBS', 'RBS', 'Liver Function Test', 'VBG', 'ABG'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2509" data-mp="39uhoec89ln0s">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2510" data-mp="3es15rl09efwy" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2511" data-mp="wqgeuaxs3xj5">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2512" data-mp="rpiy82sf3bjr">
                      SEROLOGY
                    </h4>
                    {['HIV', 'Syphilis', 'RPR', 'HBs Ag', 'HCV', 'Hepatitis Profile', 'Gravindex (Pregnancy Test)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2513" data-mp="902ii885vcir">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2514" data-mp="hzpo767xb4hm" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2515" data-mp="2kme8agyeunnz">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2516" data-mp="2kn63hqo3ijdq">
                      ENDOCRINOLOGY
                    </h4>
                    {['Hormonal Profile', 'Testosterone', 'Anti-Müllerian Hormone (AMH)', 'Beta HCG (Quantitative)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2517" data-mp="z88o4avmkdso">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2518" data-mp="145s2dasboqkm" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2519" data-mp="13uby6zs9cmkw">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2520" data-mp="18n56tyqukf29">
                      URINE / FERTILITY
                    </h4>
                    {['Urinalysis', 'Urine Microscopy', 'Urine Culture', 'Sperm Analysis', 'Sperm Culture', 'PAP Smear'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2521" data-mp="2pgk2q1tiaka2">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2522" data-mp="2fhbb3fm45hqv" />{' '}
                        {test}
                      </label>)}
                  </div>
                  <div data-id="element-2523" data-mp="1d51fruam0cts">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2524" data-mp="1d4i6t32xsyrw">
                      MICROBIOLOGY / SWABS
                    </h4>
                    {['HVS', 'Urethral Swab', 'Stool Microscopy'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2525" data-mp="146g2oenwpq0e">
                          <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2526" data-mp="1vwkjk3cgjuwv" />{' '}
                          {test}
                        </label>)}
                  </div>
                  <div data-id="element-2527" data-mp="hvy7knemcq3h">
                    <h4 className="font-medium mb-2 text-sm text-gray-700" data-id="element-2528" data-mp="hwhdf5qzvo15">
                      OTHER
                    </h4>
                    {['DOA 6 (Drugs of Abuse)'].map(test => <label key={test} className="flex items-center mb-1 text-sm" data-id="element-2529" data-mp="3lc4xzs80wqub">
                        <input type="checkbox" className="mr-2" checked={selectedTests.includes(test)} onChange={e => {
                    if (e.target.checked) setSelectedTests([...selectedTests, test]);else setSelectedTests(selectedTests.filter(t => t !== test));
                  }} data-id="element-2530" data-mp="3gfemli75yjf8" />{' '}
                        {test}
                      </label>)}
                  </div>
                </div>
                <div className="mt-6" data-id="element-2531" data-mp="3p9kgbzzzdhxa">
                  <Input label="Other Tests (Free Text)" value={otherTests} onChange={e => setOtherTests(e.target.value)} placeholder="Enter any other tests required..." data-id="element-2532" data-mp="3pan5o3hnncb3" />
                </div>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-2533" data-mp="7udmgheemxq8">
                <Button variant="outline" onClick={() => setShowLabModal(false)} data-id="element-2534" data-mp="bu619bue6041">
                  Cancel
                </Button>
                <Button onClick={handleRequestLab} data-id="element-2535" data-mp="ltwt15rki10f">Send to Laboratory</Button>
              </div>
            </div>
          </div>}

        {/* Rad Modal */}
        {showRadModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50" data-id="element-2536" data-mp="qwnx2ha030f3">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full" data-id="element-2537" data-mp="lzdzufgddkgp">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center" data-id="element-2538" data-mp="hxj37o9dvv99">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-2539" data-mp="1enjk713ih7g0">
                  Request Radiology Scan
                </h3>
                <button onClick={() => setShowRadModal(false)} data-id="element-2540" data-mp="19oqj685ev8wq">
                  <X className="w-5 h-5 text-gray-400" data-id="element-2541" data-mp="14wyslxm9936l" />
                </button>
              </div>
              <div className="p-6" data-id="element-2542" data-mp="10weqefmtr9dl">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2543" data-mp="10vdfcb5t6mvv">
                  Scan Type <span className="text-red-500" data-id="element-2544" data-mp="1xfcecyubgypb">*</span>
                </label>
                <select required value={scanType} onChange={e => setScanType(e.target.value)} className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm" data-id="element-2545" data-mp="1sm03drik8qf1">
                  <option value="" data-id="element-2546" data-mp="3d30ubbjvw2i0">— Select a scan —</option>
                  <option value="Pelvic Scan" data-id="element-2547" data-mp="3i3546iqadyz9">Pelvic Scan</option>
                  <option value="Anomaly Scan" data-id="element-2548" data-mp="344owopklo4g2">Anomaly Scan</option>
                  <option value="OBS Scan" data-id="element-2549" data-mp="346i44731esp4">OBS Scan</option>
                  <option value="Abdominal Scan" data-id="element-2550" data-mp="394jm0ooq8lz0">Abdominal Scan</option>
                  <option value="Abdomen/Pelvis Scan" data-id="element-2551" data-mp="2u6higehp40hr">
                    Abdomen and Pelvic Scan (Abdomen/Pelvis Scan)
                  </option>
                  <option value="CXR (Chest X-Ray)" data-id="element-2552" data-mp="2z5udvnmmufvy">CXR (Chest X-Ray)</option>
                  <option value="HSG (Hysterosalpingography)" data-id="element-2553" data-mp="sfxqj85a5eca">
                    HSG (Hysterosalpingography)
                  </option>
                </select>
              </div>
              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50" data-id="element-2554" data-mp="2hvgs92fu5afr">
                <Button variant="outline" onClick={() => setShowRadModal(false)} data-id="element-2555" data-mp="2cxoonb5tvzvq">
                  Cancel
                </Button>
                <Button onClick={handleRequestRad} className="bg-purple-600 hover:bg-purple-700" data-id="element-2556" data-mp="2mvcsqrpbqjqk">
                  Send to Radiology
                </Button>
              </div>
            </div>
          </div>}

        {/* Rx Modal */}
        {showRxModal && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto" data-id="element-2557" data-mp="3ajmdrrepr3gn">
            <div className="min-h-full flex items-start sm:items-center justify-center p-4" data-id="element-2558" data-mp="35lb3jfkiaqxw">
              <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[calc(100vh-2rem)] flex flex-col my-4" data-id="element-2559" data-mp="1g5uv62eep992">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 bg-white rounded-t-lg z-10" data-id="element-2560" data-mp="1b8uxofvmhetk">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-2561" data-mp="ud7cv557appg">
                    Prescribe Medication
                  </h3>
                  <button onClick={() => setShowRxModal(false)} data-id="element-2562" data-mp="z5ibm4o7g5gf">
                    <X className="w-5 h-5 text-gray-400" data-id="element-2563" data-mp="1430mw1pp2wrl" />
                  </button>
                </div>
                <div className="p-6 overflow-y-auto flex-1" data-id="element-2564" data-mp="6g1l5ck5aymu">
                  <div className="grid grid-cols-2 gap-4" data-id="element-2565" data-mp="6hmkdfhen5r1">
                    <Input label="Drug Name" className="col-span-2" value={rxForm.drugName} onChange={e => setRxForm({
                  ...rxForm,
                  drugName: e.target.value
                })} required data-id="element-2566" data-mp="bfo31zax531z" />
                    <Input label="Dose" placeholder="e.g. 500mg" value={rxForm.dose} onChange={e => setRxForm({
                  ...rxForm,
                  dose: e.target.value
                })} required data-id="element-2567" data-mp="1wq4mfr60qivz" />
                    <Select label="Route" options={[{
                  value: 'PO',
                  label: 'Oral (PO)'
                }, {
                  value: 'IV',
                  label: 'Intravenous (IV)'
                }, {
                  value: 'IM',
                  label: 'Intramuscular (IM)'
                }, {
                  value: 'Topical',
                  label: 'Topical'
                }, {
                  value: 'Vaginal',
                  label: 'Vaginal'
                }, {
                  value: 'Rectal',
                  label: 'Rectal'
                }]} value={rxForm.route} onChange={e => setRxForm({
                  ...rxForm,
                  route: e.target.value
                })} data-id="element-2568" data-mp="32j9i2t21g75m" />
                    <Input label="Frequency" placeholder="e.g. BD, TDS" value={rxForm.frequency} onChange={e => setRxForm({
                  ...rxForm,
                  frequency: e.target.value
                })} required data-id="element-2569" data-mp="h1blvjkovhj9" />
                    <Input label="No. of Days" type="number" value={rxForm.days} onChange={e => setRxForm({
                  ...rxForm,
                  days: e.target.value
                })} required data-id="element-2570" data-mp="2evyx7id7vs4c" />
                  </div>
                  <div className="mt-4 flex justify-end" data-id="element-2571" data-mp="1zyonadg82uaq">
                    <Button onClick={handleAddRx} variant="outline" className="text-green-600 border-green-600 hover:bg-green-50" data-id="element-2572" data-mp="1zz86dxhlzoqc">
                      + Add to Prescription List
                    </Button>
                  </div>

                  {selectedEncounter.prescriptions.length > 0 && <div className="mt-6 border-t border-gray-200 pt-4" data-id="element-2573" data-mp="3laq2rzdac14p">
                      <div className="flex justify-between items-center mb-2" data-id="element-2574" data-mp="2vnciucoquih7">
                        <h4 className="font-medium text-sm" data-id="element-2575" data-mp="2qpazdqneoh57">
                          Current List ({selectedEncounter.prescriptions.length}{' '}
                          of 20)
                        </h4>
                        {selectedEncounter.prescriptions.length >= 20 && <span className="text-xs text-amber-600 font-medium" data-id="element-2576" data-mp="2cxnskwvu7q86">
                            Maximum reached
                          </span>}
                      </div>
                      <ul className="space-y-2 max-h-64 overflow-y-auto pr-1 border border-gray-200 rounded p-2 bg-gray-50" data-id="element-2577" data-mp="2mn7d06z77s55">
                        {selectedEncounter.prescriptions.map((rx, idx) => <li key={rx.id} className="text-sm bg-white p-2 rounded flex justify-between items-center border border-gray-100 gap-2" data-id="element-2578" data-mp="1u6wjzxjoc3uu" data-mpi={idx}>
                            <span className="flex-1 min-w-0 truncate" data-id="element-2579" data-mp="4p96kz4za4gj">
                              <span className="text-gray-400 mr-2" data-id="element-2580" data-mp="9h9djbjgtxmm">
                                {idx + 1}.
                              </span>
                              {rx.drugName} - {rx.dose}
                            </span>
                            <span className="text-gray-500 text-xs whitespace-nowrap" data-id="element-2581" data-mp="3vwvg38edp15b">
                              {rx.frequency} x {rx.days} days
                            </span>
                            {rx.status === 'Pending' ? <button onClick={() => handleDeletePrescription(rx.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50 rounded p-1 transition-colors flex-shrink-0" title="Remove this drug" aria-label={`Remove ${rx.drugName}`} data-id="element-2582" data-mp="efudraypwhb2">
                                <Trash2 className="w-4 h-4" data-id="element-2583" data-mp="3jfb6x5609oi3" />
                              </button> : <span className="text-xs text-gray-400 flex-shrink-0 italic" data-id="element-2584" data-mp="3enb1ix7623e4">
                                {rx.status}
                              </span>}
                          </li>)}
                      </ul>
                    </div>}
                </div>
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3 bg-gray-50 flex-shrink-0 sticky bottom-0 rounded-b-lg z-10" data-id="element-2585" data-mp="196kwzccdvs1">
                  <Button onClick={() => setShowRxModal(false)} className="bg-blue-600 hover:bg-blue-700 text-white" data-id="element-2586" data-mp="3sdwksn0j8fcj">
                    Done
                  </Button>
                </div>
              </div>
            </div>
          </div>}

        {/* Image Zoom Modal */}
        {zoomImage && <div className="fixed inset-0 bg-black bg-opacity-90 flex flex-col items-center justify-center z-50" onClick={() => setZoomImage(null)} data-id="element-2587" data-mp="shhlfhf3zlo6">
            <div className="absolute top-4 right-4 flex items-center gap-3 z-10" data-id="element-2588" data-mp="11hms1pchag1a">
              <span className="text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded" data-id="element-2589" data-mp="16835vak7o3o6">
                Scroll to zoom • Click outside to close
              </span>
              <button className="text-white hover:text-gray-300 bg-black bg-opacity-50 rounded-full p-2" onClick={() => setZoomImage(null)} data-id="element-2590" data-mp="1b6wm3aqvu8s1">
                <X className="w-6 h-6" data-id="element-2591" data-mp="1k8dz2k8ztuci" />
              </button>
            </div>
            <div className="max-w-[90vw] max-h-[85vh] overflow-auto rounded-lg" onClick={e => e.stopPropagation()} data-id="element-2592" data-mp="1p5w8scuu4dlk">
              <img src={zoomImage} alt="Radiology Scan — Zoom up to 200%" className="min-w-[60vw] max-w-none" style={{
            width: '180%',
            cursor: 'grab'
          }} data-id="element-2593" data-mp="3qyc4l9u4knxj" />
            </div>
            <p className="text-gray-400 text-xs mt-3" data-id="element-2594" data-mp="174ifr5ua17j8">
              Image displayed at up to 200% zoom. Scroll within the frame to
              pan.
            </p>
          </div>}

        {/* ─── TEST FILE MODAL (View File per investigation) ─── */}
        {viewingTest && selectedEncounter && (() => {
        const patient = getPatient(selectedEncounter.patientId);
        if (!patient) return null;
        const lab = viewingTest.kind === 'lab' ? selectedEncounter.labRequests.find(l => l.id === viewingTest.labId) : null;
        const rad = viewingTest.kind === 'rad' ? selectedEncounter.radiologyRequests.find(r => r.id === viewingTest.radId) : null;
        if (!lab && !rad) return null;
        const focusedTest = viewingTest.kind === 'lab' ? viewingTest.testName : null;
        // Filter lab.results by the requested test name when possible
        const labResultsFiltered = lab && lab.results ? focusedTest ? Object.fromEntries(Object.entries(lab.results).filter(([k]) => k.toLowerCase().includes(focusedTest.toLowerCase()) || focusedTest.toLowerCase().includes(k.toLowerCase()) ||
        // FBC sub-tests, urinalysis (UA_), testosterone — keep all if focused test is one of these umbrella tests
        ['fbc', 'urinalysis', 'testosterone', 'sperm analysis'].some(u => focusedTest.toLowerCase().includes(u)))) : lab.results : null;
        return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-2595" data-mp="1n8fsm98zw7ep">
                <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-2596" data-mp="1icgx81p91p4s">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-2597" data-mp="14izf8lc7okgv">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-2598" data-mp="zl7mjp3qsubm">
                      {lab ? `Laboratory Result — ${focusedTest || lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).join(', ')}` : `Radiology Result — ${rad?.scanType}`}
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-2599" data-mp="328mnybn653c6">
                      <Button variant="outline" size="sm" onClick={handlePrintTestModal} data-id="element-2600" data-mp="366u4b5dq02mu">
                        <Printer className="w-4 h-4 mr-1" data-id="element-2601" data-mp="3g59xrf07znzi" /> Print
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleDownloadTestPdf} disabled={isGeneratingTestPdf} data-id="element-2602" data-mp="2ijccj9ugh4jv">
                        <Download className="w-4 h-4 mr-1" data-id="element-2603" data-mp="2senq3ou4o5q1" />
                        {isGeneratingTestPdf ? 'Generating...' : 'Download PDF'}
                      </Button>
                      <button onClick={closeTestModal} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-2604" data-mp="1zra2ihcbkmil">
                        <X className="w-5 h-5 text-gray-500" data-id="element-2605" data-mp="1ei1wg0u3inn9" />
                      </button>
                    </div>
                  </div>

                  <div ref={testModalRef} className="p-8 bg-white" data-id="element-2606" data-mp="15gu9ug0xzb90">
                    <div className="flex justify-between items-start mb-6 border-b-2 border-black pb-4" data-id="element-2607" data-mp="15in8b81eip5x">
                      <div className="flex items-center" data-id="element-2608" data-mp="19k0e5i4huscu">
                        <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-2609" data-mp="vpzbidaf8l77" />
                        <div data-id="element-2610" data-mp="qr4ungw0l6pq">
                          <h1 className="text-xl font-bold text-pink-600 uppercase" data-id="element-2611" data-mp="qsfy1fljscgb">
                            BMED MEDICAL CENTRE
                          </h1>
                          <p className="text-xs italic text-gray-600" data-id="element-2612" data-mp="hqq4o483v3fw">
                            Caring Healing Leading
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm" data-id="element-2613" data-mp="3pc0svdtd0ydv">
                        <p className="font-bold uppercase border-b border-black pb-1 mb-2" data-id="element-2614" data-mp="3pdlmn17uodao">
                          {lab ? 'Laboratory Report' : 'Radiology Report'}
                        </p>
                        <p data-id="element-2615" data-mp="36dr0qmqfkru">
                          <span className="font-semibold" data-id="element-2616" data-mp="h3skq7k0qepm">Patient:</span>{' '}
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p data-id="element-2617" data-mp="c2v1drn9vemj">
                          <span className="font-semibold" data-id="element-2618" data-mp="qtlt6zov6dpn">File No:</span>{' '}
                          {patient.bmcFileNumber}
                        </p>
                        <p data-id="element-2619" data-mp="lvkboj736mga">
                          <span className="font-semibold" data-id="element-2620" data-mp="zujnurdex8vk">Age/Sex:</span>{' '}
                          {patient.age} / {patient.sex}
                        </p>
                        <p data-id="element-2621" data-mp="ux1onsp7680r">
                          <span className="font-semibold" data-id="element-2622" data-mp="utm6gh088iv4">Date:</span>{' '}
                          {new Date((lab?.completedAt || rad?.completedAt) ?? Date.now()).toLocaleString()}
                        </p>
                        {lab?.performedBy && <p data-id="element-2623" data-mp="6ahbaw217z25">
                            <span className="font-semibold" data-id="element-2624" data-mp="6aqplmdpshs0">Performed By:</span>{' '}
                            {lab.performedBy}
                          </p>}
                      </div>
                    </div>

                    {lab && <div data-id="element-2625" data-mp="l7rjnwjdootn">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-2626" data-mp="l7hrc2av25ks">
                          {focusedTest || 'Tests Performed'}
                        </h2>

                        {/* ─── Structured FBC Form ─── */}
                        {focusedTest && isFBCTest(focusedTest) ? <div className="space-y-4" data-id="element-2627" data-mp="2r06luwvpcyp1">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2628" data-mp="2jgp33lqa8ctj">
                              <h3 className="font-bold uppercase" data-id="element-2629" data-mp="2epp2a5e1lalr">
                                Full Blood Count (FBC) Report
                              </h3>
                            </div>
                            {fbcStructure.map(cat => <div key={cat.category} className="border border-black" data-id="element-2630" data-mp="25qljl2isex7s">
                                <div className="bg-gray-100 p-2 font-bold border-b border-black text-xs uppercase" data-id="element-2631" data-mp="2xh83q7sf4glo">
                                  {cat.category}
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-2632" data-mp="2oeqjfywbpy1b">
                                  <thead data-id="element-2633" data-mp="1i07tvinodagd">
                                    <tr className="bg-gray-50" data-id="element-2634" data-mp="1hwto00fsyj46">
                                      <th className="border border-black p-1.5 text-left" data-id="element-2635" data-mp="1cza70srpzs8j">
                                        Test
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-2636" data-mp="35nrkd5kx4tka">
                                        Result
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-2637" data-mp="2vx6mxlgzdv2y">
                                        Reference Range
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-24" data-id="element-2638" data-mp="3omvnxytoxv4k">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-2639" data-mp="3fns3aqevl7ny">
                                    {cat.tests.map(t => <tr key={t.name} data-id="element-2640" data-mp="1z9s7nrn2c46i">
                                        <td className="border border-black p-1.5" data-id="element-2641" data-mp="247a6uqba351b">
                                          {t.name}
                                        </td>
                                        <td className="border border-black p-1.5 font-bold" data-id="element-2642" data-mp="1rooamcbwhz9o">
                                          {lab.results && lab.results[t.name] || '—'}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2643" data-mp="1d01wgdy0fdk1">
                                          {t.range}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2644" data-mp="1mp3ahykzvkvd">
                                          {t.unit}
                                        </td>
                                      </tr>)}
                                  </tbody>
                                </table>
                              </div>)}
                          </div> : focusedTest && isUrinalysisTest(focusedTest) ? <div data-id="element-2645" data-mp="2u9h0fx5auyaw">
                            <div className="text-center border-b-2 border-black pb-2 mb-6" data-id="element-2646" data-mp="2u8fegxmqwree">
                              <h3 className="font-bold uppercase" data-id="element-2647" data-mp="2z3l9cly4lgem">
                                Urinalysis Report
                              </h3>
                            </div>
                            <div className="max-w-md mx-auto space-y-2" data-id="element-2648" data-mp="26ensmn9kbrnz">
                              {urinalysisTests.map(t => {
                      const key = `UA_${t.name}`;
                      const value = lab.results && (lab.results[key] || lab.results[t.name]) || '';
                      return <div key={t.name} className="flex items-center text-sm" data-id="element-2649" data-mp="3vq6dz3pyrxnn">
                                    <span className="w-16 font-bold" data-id="element-2650" data-mp="4rqtzxd0bz4q">
                                      {t.name}
                                    </span>
                                    <span className="mx-3 text-gray-400" data-id="element-2651" data-mp="3s4t4ldo10sh7">
                                      -
                                    </span>
                                    <span className="flex-1 border-b border-gray-400 pb-0.5 font-medium" data-id="element-2652" data-mp="3n33r7rdc02py">
                                      {value || '\u00A0'}
                                    </span>
                                  </div>;
                    })}
                            </div>
                            <div className="max-w-md mx-auto mt-10 grid grid-cols-2 gap-4 text-sm" data-id="element-2653" data-mp="1bg5qxn0svst4">
                              <div data-id="element-2654" data-mp="17diz3m44q7vx">
                                <p className="font-semibold mb-2" data-id="element-2655" data-mp="17ek71hpume9f">
                                  Dr.'s Name:
                                </p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-2656" data-mp="1uo5o7atw60gr">
                                  {lab.results && lab.results['UA_DR_NAME'] || '\u00A0'}
                                </p>
                              </div>
                              <div data-id="element-2657" data-mp="16yt7lpw8iq4f">
                                <p className="font-semibold mb-2" data-id="element-2658" data-mp="16xrtfcjx9nih">Signature:</p>
                                <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-2659" data-mp="1byfm02nsucet">
                                  {lab.results && lab.results['UA_DR_SIG'] || '\u00A0'}
                                </p>
                              </div>
                            </div>
                          </div> : focusedTest && isTestosteroneTest(focusedTest) ? <div className="space-y-2" data-id="element-2660" data-mp="2xaqqp9y31mdf">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2661" data-mp="37exnz0sujylk">
                              <h3 className="font-bold uppercase" data-id="element-2662" data-mp="32gcox4pbzkyp">
                                Testosterone Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2663" data-mp="3g9c04ui2yt0g">
                              <thead data-id="element-2664" data-mp="3bfhj5ytv0s52">
                                <tr className="bg-gray-100" data-id="element-2665" data-mp="3q5f7cershhu9">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2666" data-mp="3q7rwbbvyssgt">
                                    Test
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2667" data-mp="2zo5rsutrws8">
                                    Result
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2668" data-mp="24rsukvh12dyq">
                                    Reference (Male)
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2669" data-mp="fzs1sejgmx2c">
                                    Reference (Female)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2670" data-mp="b2tas4v3f68d">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2671" data-mp="yxvyg9i5v3n9">
                                {testosteroneTests.map(t => <tr key={t.name} data-id="element-2672" data-mp="u0cgozm988qk">
                                    <td className="border border-black p-1.5" data-id="element-2673" data-mp="txqc98unu9ck">
                                      {t.name}
                                    </td>
                                    <td className="border border-black p-1.5 font-bold" data-id="element-2674" data-mp="13pw48ikwwqrr">
                                      {lab.results && (lab.results[`TESTO_${t.name}`] || lab.results[t.name]) || '—'}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2675" data-mp="21b74tzaokv22">
                                      {t.rangeMale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2676" data-mp="1rchle234z2jp">
                                      {t.rangeFemale}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-2677" data-mp="1djjm44nkoqav">
                                      {t.unit}
                                    </td>
                                  </tr>)}
                              </tbody>
                            </table>
                          </div> : focusedTest && isHormonalProfileTest(focusedTest) ? <div data-id="element-2678" data-mp="zka5j3vgfu6d">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2679" data-mp="znef6grovi4f">
                              <h3 className="font-bold uppercase" data-id="element-2680" data-mp="31cu332mjp5e2">
                                Hormonal Profile Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2681" data-mp="2rl7hqdghtpxl">
                              <thead data-id="element-2682" data-mp="2nknovm2yd8h9">
                                <tr className="bg-gray-100" data-id="element-2683" data-mp="2nhjcw2j8x8g1">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2684" data-mp="3k93bpaeoh3qo">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2685" data-mp="3b7vq9sx9fxe0">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2686" data-mp="1zti8n5t3oe6f">
                                    Reference Range
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2687" data-mp="1ux1hs3uinlpn">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2688" data-mp="jc9pvxd9d68a">
                                {hormonalProfileTests.map(h => <Fragment key={h.key} data-id="element-2689" data-mp="o52r4b9ipwfm">
                                    <tr className="align-top" data-id="element-2690" data-mp="t349sv317tqk">
                                      <td className="border border-black p-1.5 font-medium" data-id="element-2691" data-mp="t2vznayan2j2">
                                        {h.name}
                                      </td>
                                      <td className="border border-black p-1.5 font-bold" data-id="element-2692" data-mp="132mrf4vgz3fg">
                                        {lab.results && (lab.results[`HP_${h.key}`] || lab.results[h.name] || lab.results[h.key]) || '—'}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-2693" data-mp="29ii99lqje1ge">
                                        {h.ranges.map(r => <div key={r.label} data-id="element-2694" data-mp="2nhrejqd7fb5l">
                                            <span className="font-medium" data-id="element-2695" data-mp="2iiolz5eeuxei">
                                              {r.label}:
                                            </span>{' '}
                                            {r.value}
                                          </div>)}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-2696" data-mp="1zsoyuo24rsdq">
                                        {h.unit}
                                      </td>
                                    </tr>
                                    <tr data-id="element-2697" data-mp="174jcjwvfhvbs">
                                      <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2698" data-mp="173i0bp2of2sh">
                                        Method: {h.method}
                                      </td>
                                    </tr>
                                  </Fragment>)}
                              </tbody>
                            </table>
                          </div> : focusedTest && isAMHTest(focusedTest) ? <div data-id="element-2699" data-mp="3shg6s9o1957u">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2700" data-mp="3sg4t9i4scj3j">
                              <h3 className="font-bold uppercase" data-id="element-2701" data-mp="1a3lpjk147mn">
                                Anti Mullerian Hormone (AMH) Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2702" data-mp="2m25g6330prp8">
                              <thead data-id="element-2703" data-mp="rlf545ao8ocz">
                                <tr className="bg-gray-100" data-id="element-2704" data-mp="rkdwe7h4odyf">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2705" data-mp="mlaqylx8k5px">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2706" data-mp="dth3sgn7bwrd">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2707" data-mp="8sb9iedw81fq">
                                    Reference Range (by age, ng/mL)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2708" data-mp="3v1w1461lt4um">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2709" data-mp="1p84gh5r9kdbi">
                                <tr className="align-top" data-id="element-2710" data-mp="1p95sbcg3t3ua">
                                  <td className="border border-black p-1.5 font-medium" data-id="element-2711" data-mp="376snpnu3o3wb">
                                    {amhStructure.name}
                                  </td>
                                  <td className="border border-black p-1.5 font-bold" data-id="element-2712" data-mp="3gw2jbdo5k4p6">
                                    {lab.results && (lab.results[amhStructure.storageKey] || lab.results[focusedTest] || lab.results['AMH'] || lab.results['Anti-Müllerian Hormone'] || lab.results['Anti Mullerian Hormone (AMH)']) || '—'}
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-2713" data-mp="ck8biv8v0nqx">
                                    {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-2714" data-mp="3pakm95johkqs">
                                        <span className="font-medium" data-id="element-2715" data-mp="3kay54l0qbkcp">
                                          {a.ageBand}:
                                        </span>{' '}
                                        {a.range}
                                      </div>)}
                                  </td>
                                  <td className="border border-black p-1.5 text-xs" data-id="element-2716" data-mp="btbjivbfnwpr">
                                    {amhStructure.unit}
                                  </td>
                                </tr>
                                <tr data-id="element-2717" data-mp="ktgpr24w4p2c">
                                  <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2718" data-mp="gtg1yqvwj1im">
                                    Method: {amhStructure.method}
                                  </td>
                                </tr>
                              </tbody>
                            </table>
                            <div className="mt-3 border border-amber-300 bg-amber-50 rounded p-3 text-xs" data-id="element-2719" data-mp="2ci7oth343njw">
                              <p className="font-bold uppercase mb-1" data-id="element-2720" data-mp="1ypi37drhwknb">
                                Interpretation
                              </p>
                              <ul className="list-disc pl-5 space-y-0.5 text-gray-700" data-id="element-2721" data-mp="1ot594es1g4s1">
                                {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-2722" data-mp="1fsqdalhg77aa" data-mpi={i}>{line}</li>)}
                              </ul>
                            </div>
                          </div> : focusedTest && isHCGTest(focusedTest) ? <div data-id="element-2723" data-mp="3a7aqye7cq2k6">
                            <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-2724" data-mp="3f5uf8l9f3qfp">
                              <h3 className="font-bold uppercase" data-id="element-2725" data-mp="3f3hygbfebm48">
                                Beta HCG (Quantitative) Report
                              </h3>
                            </div>
                            <table className="w-full border-collapse text-sm" data-id="element-2726" data-mp="pourauiadmbz">
                              <thead data-id="element-2727" data-mp="uozbovmbf17h">
                                <tr className="bg-gray-100" data-id="element-2728" data-mp="zgqs4dbrbqjw">
                                  <th className="border border-black p-1.5 text-left" data-id="element-2729" data-mp="zibmo2y2n9hr">
                                    Test Description
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-32" data-id="element-2730" data-mp="18gdrktkxemab">
                                    Value
                                  </th>
                                  <th className="border border-black p-1.5 text-left" data-id="element-2731" data-mp="1ig4pl58p3jf5">
                                    Reference Range (mIU/mL)
                                  </th>
                                  <th className="border border-black p-1.5 text-left w-20" data-id="element-2732" data-mp="3k6ycyeoxd4xp">
                                    Unit
                                  </th>
                                </tr>
                              </thead>
                              <tbody data-id="element-2733" data-mp="1wjtyt2daywdz">
                                {(() => {
                        const hcgValue = lab.results && (lab.results[hcgStructure.storageKey] || lab.results[focusedTest] || lab.results['HCG'] || lab.results['Beta HCG']) || '';
                        const abnormal = isHCGAbnormal(hcgValue);
                        return <>
                                      <tr className="align-top" data-id="element-2734" data-mp="zve6iyit68x6">
                                        <td className="border border-black p-1.5 font-medium" data-id="element-2735" data-mp="zsrq09dxwj2t">
                                          {hcgStructure.name}
                                        </td>
                                        <td className={`border border-black p-1.5 font-bold ${abnormal ? 'text-red-600 bg-red-50' : ''}`} data-id="element-2736" data-mp="11cb6ceyywo1h">
                                          {hcgValue || '—'}
                                          {abnormal && <div className="text-xs font-normal mt-0.5" data-id="element-2737" data-mp="dlnw3n1kxmbr">
                                              Above normal threshold (&gt; 6
                                              mIU/mL)
                                            </div>}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2738" data-mp="3qt8u7mx39t6t">
                                          {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-2739" data-mp="3vrk2vubndxng">
                                              <span className="font-medium" data-id="element-2740" data-mp="3c5qoqmbpcujc">
                                                {r.label}:
                                              </span>{' '}
                                              {r.range}
                                            </div>)}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-2741" data-mp="3v4w497xnurwj">
                                          {hcgStructure.unit}
                                        </td>
                                      </tr>
                                      <tr data-id="element-2742" data-mp="8pa4tjy1o4i2">
                                        <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-2743" data-mp="3r07p3srettt">
                                          Method: {hcgStructure.method}
                                        </td>
                                      </tr>
                                    </>;
                      })()}
                              </tbody>
                            </table>
                          </div> : labResultsFiltered && Object.keys(labResultsFiltered).length > 0 ? <table className="w-full border-collapse text-sm" data-id="element-2744" data-mp="1oac1x0v9t6d8">
                            <thead data-id="element-2745" data-mp="1t8x0kvuvjhzk">
                              <tr className="bg-gray-100" data-id="element-2746" data-mp="1t9yer976skli">
                                <th className="border border-black p-2 text-left" data-id="element-2747" data-mp="1y5vtjb2sxiw6">
                                  Parameter
                                </th>
                                <th className="border border-black p-2 text-left" data-id="element-2748" data-mp="39m3ytk9mb8bj">
                                  Result
                                </th>
                              </tr>
                            </thead>
                            <tbody data-id="element-2749" data-mp="11zanppkljc0p">
                              {Object.entries(labResultsFiltered).map(([k, v]) => <tr key={k} data-id="element-2750" data-mp="y14n6zacpso8">
                                    <td className="border border-black p-2 font-medium" data-id="element-2751" data-mp="t70fgdt9hgm3">
                                      {k}
                                    </td>
                                    <td className="border border-black p-2" data-id="element-2752" data-mp="jads9idgqdgw">
                                      {v}
                                    </td>
                                  </tr>)}
                            </tbody>
                          </table> : <p className="text-gray-500 italic" data-id="element-2753" data-mp="5c61js70s8e6">
                            No structured results recorded for this test.
                          </p>}

                        {lab.labComments && <div className="mt-6 border border-gray-300 bg-gray-50 rounded p-4" data-id="element-2754" data-mp="2is7ee6im7sog">
                            <h3 className="font-bold uppercase mb-2 text-sm" data-id="element-2755" data-mp="2njz8gr0zcgjc">
                              Comments / Observations / Interpretation
                            </h3>
                            <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-id="element-2756" data-mp="2er5fxrwivknj">
                              {lab.labComments}
                            </pre>
                          </div>}
                      </div>}

                    {rad && <div data-id="element-2757" data-mp="2sj227fbif0c0">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-2758" data-mp="2shqytglz7ulf">
                          {rad.scanType}
                        </h2>
                        {rad.radiographerName && <p className="text-sm mb-3" data-id="element-2759" data-mp="1h2lvcae0ts0r">
                            <span className="font-semibold" data-id="element-2760" data-mp="1m2gmmf1pkjmc">
                              Sonographer / Radiographer:
                            </span>{' '}
                            {rad.radiographerName}
                          </p>}
                        {rad.report && <div className="mb-4 border border-black p-4" data-id="element-2761" data-mp="1njdkvr27yqoj">
                            <pre className="whitespace-pre-wrap font-sans text-sm" data-id="element-2762" data-mp="1nh9khj3j49t0">
                              {rad.report}
                            </pre>
                          </div>}
                        {rad.images && rad.images.length > 0 && <div data-id="element-2763" data-mp="qv7mhlvw4uip">
                            <div className="flex justify-between items-center mb-3 print:hidden" data-id="element-2764" data-mp="14khsml9x8mdc">
                              <h3 className="font-bold" data-id="element-2765" data-mp="zub74uaphhzb">Scan Images</h3>
                              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg" data-id="element-2766" data-mp="zryf1ob8hr8j">
                                <button onClick={() => setTestImageZoom(z => Math.max(10, z - 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom out" data-id="element-2767" data-mp="c244jdee0erg">
                                  <ZoomOut className="w-4 h-4" data-id="element-2768" data-mp="kcw5ghepuio" />
                                </button>
                                <input type="range" min={10} max={200} step={10} value={testImageZoom} onChange={e => setTestImageZoom(Number(e.target.value))} className="w-32" data-id="element-2769" data-mp="5hmvpoyifmk8" />
                                <button onClick={() => setTestImageZoom(z => Math.min(200, z + 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom in" data-id="element-2770" data-mp="t88s2iz5jiee">
                                  <ZoomIn className="w-4 h-4" data-id="element-2771" data-mp="2wh2dy2z3uprq" />
                                </button>
                                <span className="text-sm font-medium w-12 text-right" data-id="element-2772" data-mp="2rh69k2mhwwbn">
                                  {testImageZoom}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-4 print:space-y-2" data-id="element-2773" data-mp="2hrd29dtdz3d7">
                              {rad.images.map((img, idx) => <div key={idx} className="border-2 border-gray-300 rounded overflow-auto bg-gray-50 max-h-[600px] print:max-h-none print:overflow-visible" data-id="element-2774" data-mp="3agk86xv66v8h" data-mpi={idx}>
                                  <div style={{
                        transform: `scale(${testImageZoom / 100})`,
                        transformOrigin: 'top left',
                        display: 'inline-block'
                      }} data-id="element-2775" data-mp="zz07yo7omu3o">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="block" data-id="element-2776" data-mp="1jl6a4e82p6yg" />
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </div>
              </div>;
      })()}
      </div>;
  }
  return <div className="space-y-6" data-id="element-2777" data-mp="2evufzjlocsqu">
      <div className="flex justify-between items-center" data-id="element-2778" data-mp="2jt2yjlaqiysm">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-2779" data-mp="2jrjj1wwwkrrw">
          Clinician Dashboard
        </h1>
        <div className="flex space-x-2" data-id="element-2780" data-mp="2nvr7dszg85pl">
          <Badge variant="info" className="text-sm px-3 py-1" data-id="element-2781" data-mp="2su15abck5kew">
            {pendingEncounters.length} Patients Waiting
          </Badge>
          <Badge variant="success" className="text-sm px-3 py-1 bg-green-100 text-green-800" data-id="element-2782" data-mp="2xr1cipl58t7l">
            {completedEncounters.length} Completed
          </Badge>
        </div>
      </div>

      <div className="border-b border-gray-200" data-id="element-2783" data-mp="37ki4rp5mu095">
        <nav className="-mb-px flex space-x-8" data-id="element-2784" data-mp="3bkscb692oyq4">
          <button onClick={() => setActiveTab('queue')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'queue' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2785" data-mp="3bhxsngxvvhxr">
            Active Queue
          </button>
          <button onClick={() => setActiveTab('completed')} className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'completed' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2786" data-mp="1q6xckelvvmxr">
            Completed Consultations / History
          </button>
        </nav>
      </div>

      {activeTab === 'queue' && <Card noPadding data-id="element-2787" data-mp="294qh1lc5o4x9">
          <ul className="divide-y divide-gray-200" data-id="element-2788" data-mp="2935ilkmq01u4">
            {pendingEncounters.length > 0 ? pendingEncounters.map(encounter => {
          const patient = getPatient(encounter.patientId);
          if (!patient) return null;
          const isInvestigations = encounter.stage === 'INVESTIGATIONS';
          const hasResults = isInvestigations && !encounter.pendingLab && !encounter.pendingRadiology;
          const hasDraft = !!encounter.clinicalNote;
          const isPharmacy = encounter.stage === 'PHARMACY';
          const iconBg = hasResults ? 'bg-green-100' : isPharmacy ? 'bg-pink-100' : isInvestigations ? 'bg-yellow-100' : 'bg-blue-100';
          const iconColor = hasResults ? 'text-green-600' : isPharmacy ? 'text-pink-600' : isInvestigations ? 'text-yellow-600' : 'text-blue-600';
          return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-2789" data-mp="3o0hx05h39v2x">
                    <div className="flex items-center" data-id="element-2790" data-mp="33c8pdiarqq9z">
                      <div className={`p-2 rounded-full mr-4 ${iconBg}`} data-id="element-2791" data-mp="2yeqc5fppxow6">
                        <Stethoscope className={`h-6 w-6 ${iconColor}`} data-id="element-2792" data-mp="2yce01j6n68r1" />
                      </div>
                      <div data-id="element-2793" data-mp="2th6z2j4rbi5t">
                        <p className="text-sm font-medium text-blue-600" data-id="element-2794" data-mp="2ogsp2vjlhy55">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-2795" data-mp="2kg0g83emxv9e">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <div className="flex flex-wrap gap-2 mt-1" data-id="element-2796" data-mp="2apxoe6j92ncn">
                          <Badge variant="default" data-id="element-2797" data-mp="25q1jm52qarw1">{patient.age} yrs</Badge>
                          {hasResults && <Badge variant="success" data-id="element-2798" data-mp="20qyc8dza09kw">Results Ready</Badge>}
                          {isInvestigations && encounter.pendingLab && <Badge variant="warning" data-id="element-2799" data-mp="c72jpa0ggndr">Awaiting Lab</Badge>}
                          {isInvestigations && encounter.pendingRadiology && <Badge variant="warning" data-id="element-2800" data-mp="gaihi9o9gts1">Awaiting Radiology</Badge>}
                          {isPharmacy && <Badge variant="purple" data-id="element-2801" data-mp="q5sicu2xav48">At Pharmacy</Badge>}
                          {hasDraft && encounter.stage === 'CLINICIAN' && <Badge variant="info" data-id="element-2802" data-mp="3tn0z1alwzoxd">Draft Saved</Badge>}
                        </div>
                      </div>
                    </div>
                    <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-2803" data-mp="2fg5r5wqga7b">
                      Open File
                    </Button>
                  </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-2804" data-mp="3ax2omnun9jqk">
                <Stethoscope className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-2805" data-mp="3av86tieo67qp" />
                <p className="text-lg font-medium text-gray-900" data-id="element-2806" data-mp="32v24kpk0448u">
                  No patients waiting
                </p>
              </li>}
          </ul>
        </Card>}

      {activeTab === 'completed' && <Card data-id="element-2807" data-mp="3coajxx8da5z7">
          <div className="mb-4" data-id="element-2808" data-mp="37n3dq2lvheun">
            <input type="text" placeholder="Search completed consultations by patient name or file number..." className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:outline-none" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-2809" data-mp="37mtpokke1hrl" />
          </div>
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-2810" data-mp="2ow2inpnvi3gd">
            <ul className="divide-y divide-gray-200" data-id="element-2811" data-mp="957ji5pubq3k">
              {filteredCompleted.length > 0 ? filteredCompleted.map(encounter => {
            const patient = getPatient(encounter.patientId);
            if (!patient) return null;
            return <li key={encounter.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-2812" data-mp="1r2uier31p8ac">
                      <div className="flex items-center" data-id="element-2813" data-mp="1d5o00xm8hbln">
                        <div className="p-2 rounded-full mr-4 bg-green-100" data-id="element-2814" data-mp="1i37d3u773ic0">
                          <CheckCircle className="h-6 w-6 text-green-600" data-id="element-2815" data-mp="25rqlsau5px4m" />
                        </div>
                        <div data-id="element-2816" data-mp="29tu0r9g1bjun">
                          <p className="text-sm font-medium text-blue-600" data-id="element-2817" data-mp="29t2bshgy0kdc">
                            {patient.bmcFileNumber}
                          </p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-2818" data-mp="210qt5hs2m17x">
                            {patient.firstName} {patient.lastName}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-1 text-sm text-gray-500" data-id="element-2819" data-mp="2thkxuq6njhnt">
                            <span data-id="element-2820" data-mp="2tje80fgh4k0k">
                              {new Date(encounter.startedAt).toLocaleDateString()}
                            </span>
                            <span data-id="element-2821" data-mp="jp8wf6s8u98n">•</span>
                            <span data-id="element-2822" data-mp="jqa9fgstl5t0">
                              Attended by:{' '}
                              {encounter.lastAttendingClinician || 'Unknown'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <Button onClick={() => handleSelectEncounter(encounter)} data-id="element-2823" data-mp="17g4kbstkwkam">
                        Open File
                      </Button>
                    </li>;
          }) : <li className="p-12 text-center text-gray-500" data-id="element-2824" data-mp="ydv9f1ueeowp">
                  <p className="text-lg font-medium text-gray-900" data-id="element-2825" data-mp="3n6hcpm7bp119">
                    No completed consultations found
                  </p>
                </li>}
            </ul>
          </div>
        </Card>}
    </div>;
};